import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import * as Parse from 'parse'
import { BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, pairwise, shareReplay, switchMap, take } from 'rxjs'
import { InputSelectOption } from 'src/app/shared/atoms/input/input.component'
import { RecordMap } from '../../../server/src/shared-with-client/types'
import { Utils } from '../../../server/src/shared-with-client/utils'
import { ActiveSubscription } from '../../../server/src/shared-with-client/utils/subscription'
import { FunctionNames, Params, Responses } from './../../../server/src/shared-with-client/endpoint-definitions'
import { Objects } from './../../../server/src/shared-with-client/object-definitions'
import { CustomerService } from './core/parse.service/customer'
import { Establishment, EstablishmentService } from './core/parse.service/establishment'
import { MailingService } from './core/parse.service/mailing'
import { MailingListService } from './core/parse.service/mailingList'
import { RoleService } from './core/parse.service/role'
import { EnvironmentService } from './environment.service'
import { LogRocketService } from './log-rocket.service'
import { MailingComparisonReport } from './mailing/manage/view-updated-button/view-updated-button.component'
import { Csv } from './model/Csv.model'
import { SentryErrorHandler } from './sentry.module'
import { InvalidSessionService } from './shared/dialog/invalid-session/invalid-session.service'
import { WP_DOMAIN } from './shared/utils/constants'

export interface User {
    id: string
    username: string
    firstName: string
    lastName: string
}

export interface LoggedInUser {
    id: string
    username: string
    firstName: string
    lastName: string
    phone: string
    createdAt: Date
}

@Injectable()
export class ParseService {
    public readonly user = new BehaviorSubject<LoggedInUser | undefined>(undefined)
    public readonly clients = new BehaviorSubject<User[] | undefined>(undefined)

    /** @deprecated */
    public readonly selectedEstablishment = new BehaviorSubject<Establishment | undefined>(undefined)
    public readonly selectedEstablishment$ = this.selectedEstablishment.pipe(
        filter((selectedEstablishment): selectedEstablishment is Establishment => selectedEstablishment !== undefined)
    )
    public readonly selectedParseEstablishment$ = this.selectedEstablishment$.pipe(
        switchMap((selectedEstablishment) => {
            return this.getEstablishment(selectedEstablishment.id)
        }),
        filter((establishment): establishment is Objects.Establishment.Parse => establishment !== undefined),
        shareReplay({ refCount: true, bufferSize: 1 })
    )

    public readonly uxConfig$ = this.selectedEstablishment$.pipe(
        switchMap((selectedEstablishment) => {
            return Utils.UXConfigEstablishment.getSummary(this.getUXConfigSummaryQuery, selectedEstablishment.id, false)
        }),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 })
    )

    /** @deprecated */
    public readonly lastSelectedClient = new BehaviorSubject<User | undefined>(undefined)
    public readonly selectedClient$ = new BehaviorSubject<User | undefined>(undefined)

    readonly customerService = new CustomerService()
    readonly roleService = new RoleService()
    readonly establishmentService = new EstablishmentService()
    readonly mailingListService = new MailingListService()
    readonly mailingService = new MailingService()

    async setSelectedEstablishment(id: string) {
        const establishment = await this.establishmentService.getEstablishment(id)
        if (establishment) {
            this.selectedEstablishment.next(establishment)
        }

        return establishment
    }

    // @deprecated
    public getClientListAndSelectedClient(clientList: User[]): { clientList: InputSelectOption<User>[]; selectedClient?: User } {
        const lastSelectedClient = this.lastSelectedClient.getValue()
        const details: {
            clientList: InputSelectOption<User>[]
            selectedClient?: User
        } = {
            clientList: [],
            selectedClient: undefined,
        }

        for (const c of clientList) {
            details.clientList.push(new InputSelectOption(c.firstName + ' ' + c.lastName, c))
            if (lastSelectedClient !== undefined && c.id === lastSelectedClient.id) {
                details.selectedClient = lastSelectedClient
            }
        }

        if (!details.selectedClient) {
            const loggedInUser = this.user.getValue()

            if (loggedInUser) {
                for (const client of details.clientList) {
                    if (client.value.id === loggedInUser.id) {
                        details.selectedClient = loggedInUser
                        break
                    }
                }
            }
        }

        if (!details.selectedClient) {
            const firstClientInList = details.clientList[0] ? details.clientList[0].value : undefined
            details.selectedClient = firstClientInList
        }

        return details
    }

    constructor(
        private http: HttpClient,
        private environmentService: EnvironmentService,
        private sentry: SentryErrorHandler,
        private logRocketService: LogRocketService,
        private invalidSessionService: InvalidSessionService
    ) {
        // console.log('[ Parse.Service ] - constructor - environmentService', this.environmentService);
    }

    public async init() {
        try {
            await this.initialize()
            this.sentry.init()

            // this.updateDefaultSelectedEstablishment()

            this.customerService.load.subscribeAll((loadedData) => this.onCustomerLoadUpdate(loadedData))
            this.user.pipe(take(1)).subscribe((user) => {
                this.onCurrentUserUpdate(user, false)
            })
            this.user.pipe(pairwise()).subscribe((user) => {
                const isSameUser = user[0]?.id === user[1]?.id
                this.onCurrentUserUpdate(user[1], isSameUser)
            })
            this.selectedEstablishment.subscribe(() => this.onSelectedEstablishmentUpdate())
            this.clients.subscribe((clients) => {
                if (clients) {
                    const lastSelectedClient = this.lastSelectedClient.getValue()
                    const details: {
                        // clientList: InputSelectOption<User>[];
                        selectedClient?: User
                    } = {
                        // clientList: [],
                        selectedClient: undefined,
                    }

                    for (const c of clients) {
                        // details.clientList.push(new InputSelectOption(c.firstName + ' ' + c.lastName, c));
                        if (lastSelectedClient !== undefined && c.id === lastSelectedClient.id) {
                            details.selectedClient = lastSelectedClient
                        }
                    }

                    if (!details.selectedClient) {
                        const loggedInUser = this.user.getValue()

                        if (loggedInUser) {
                            for (const client of clients) {
                                if (client.id === loggedInUser.id) {
                                    details.selectedClient = loggedInUser
                                    break
                                }
                            }
                        }
                    }

                    if (!details.selectedClient) {
                        const firstClientInList = clients[0] ? clients[0] : undefined
                        details.selectedClient = firstClientInList
                    }

                    if (details.selectedClient?.id !== this.selectedClient$.value?.id) {
                        this.selectedClient$.next(details.selectedClient)
                    }
                }
            })
            this.lastSelectedClient.subscribe((client) => {
                const selectedClient = this.selectedClient$.value
                if (selectedClient?.id !== client?.id && client) {
                    this.selectedClient$.next(client)
                }
            })
        } catch (err) {
            this.reportUIError(err, 'ParseService.init', '')
        }
    }

    private async initialize() {
        // console.log('[ Parse.Service ] - initialize')
        try {
            Parse.initialize(this.environmentService.appId)

            Parse.CoreManager.set('SERVER_URL', this.environmentService.backendUrl + '/api')

            const currentUser = Parse.User.current<Objects.User._Parse>()
            if (currentUser !== undefined && currentUser !== null) {
                const [loggedInUser] = await Promise.all([currentUser.fetch(), this.roleService.find.toPromise({ user: currentUser })])

                const user: LoggedInUser = {
                    id: loggedInUser.id,
                    firstName: loggedInUser.get('firstName') || '',
                    lastName: loggedInUser.get('lastName') || '',
                    username: loggedInUser.get('username') || '',
                    phone: loggedInUser.get('phone') || '',
                    createdAt: loggedInUser.createdAt,
                }

                this.user.next(user)
            }
        } catch (err) {
            if (err.message === 'Invalid session token') {
                await this.logOut()
            } else {
                this.reportUIError(err, 'Parse.Service.initialize()', '')
            }
        }
    }

    private onCurrentUserUpdate(user: LoggedInUser | undefined, isSameUser: boolean) {
        if (!user) {
            this.roleService.clearRoles()
        }

        if (user && !isSameUser) {
            this.updateDefaultSelectedEstablishment(user.id)
            this.roleService.find.toPromise({ user }).catch((err) => {})
        }

        const logRocket = this.logRocketService.logRocket
        if (logRocket) {
            if (user) {
                logRocket.identify(user.id, { name: user.firstName + ' ' + user.lastName, email: user.username })
            } else {
                logRocket.identify()
            }
        }
    }

    private areValuesDifferent(oldValue: { id: string }[] | { id: string } | undefined, newValue: { id: string }[] | { id: string } | undefined): boolean {
        if (oldValue === undefined || newValue === undefined) {
            if (oldValue === undefined && newValue !== undefined) {
                return true
            }
            if (oldValue !== undefined && newValue === undefined) {
                return true
            }
            return false
        }

        const oldValueList = Array.isArray(oldValue) ? oldValue : [oldValue]
        const newValueList = Array.isArray(newValue) ? newValue : [newValue]

        if (oldValueList.length !== newValueList.length) {
            return true
        }

        oldValueList.sort((a, b) => {
            if (a.id > b.id) {
                return 1
            }

            if (a.id < b.id) {
                return -1
            }

            return 0
        })

        newValueList.sort((a, b) => {
            if (a.id > b.id) {
                return 1
            }

            if (a.id < b.id) {
                return -1
            }

            return 0
        })

        let index = 0
        const length = oldValueList.length
        while (index < length) {
            const oldValueItem = oldValueList[index]
            const newValueItem = newValueList[index]

            if (oldValueItem.id !== newValueItem.id) {
                return true
            }
            index++
        }

        return false
    }

    // private onEstablishmentFindUpdate(establishments?: Establishment[]) {
    //     // console.log('[ Parse.Service ] - onEstablishmentFindUpdate()', establishments)
    //     const establishment = establishments?.length ? establishments[0] : undefined;
    //     if (this.areValuesDifferent(this.selectedEstablishment.value, establishment)) {
    //         this.selectedEstablishment.next(establishment);
    //     }
    // }

    private onSelectedEstablishmentUpdate() {
        if (this.lastSelectedClient.getValue()) {
            this.lastSelectedClient.next(undefined)
        }
        this.customerService.load.run({ establishment: this.selectedEstablishment.getValue() })
    }

    private onCustomerLoadUpdate(loadedData?: { customers: Objects.User._Parse.ForEstablishmentQuery[]; establishment: Establishment }) {
        let clientList: User[] | undefined
        const selectedEstablishment = this.selectedEstablishment.getValue()
        if (loadedData && selectedEstablishment && loadedData.establishment.id === selectedEstablishment.id) {
            clientList = []

            const lastSelectedClient = this.lastSelectedClient.getValue()
            let lastSelectedClientFound = false

            for (const c of loadedData.customers) {
                if (!lastSelectedClientFound && lastSelectedClient !== undefined) {
                    if (lastSelectedClient.id === c.id) {
                        lastSelectedClientFound = true
                    }
                }
                const client: User = {
                    id: c.id,
                    firstName: c.get('firstName') || '',
                    lastName: c.get('lastName') || '',
                    username: c.get('username') || '',
                }
                clientList.push(client)
            }

            clientList.sort((userA, userB) => {
                const sortValueA = `${userA.firstName}${userA.lastName}`.toLowerCase()
                const sortValueB = `${userB.firstName}${userB.lastName}`.toLowerCase()

                if (sortValueA > sortValueB) {
                    return 1
                }

                if (sortValueA < sortValueB) {
                    return -1
                }

                return 0
            })

            if (!lastSelectedClientFound) {
                this.lastSelectedClient.next(undefined)
            }
        }

        if (this.areValuesDifferent(this.clients.getValue(), clientList)) {
            this.clients.next(clientList)
        }
    }

    private async callCloudWithLoginHandler<R, T extends string = string, P extends Parse.Cloud.Params = object>(fn: T, params: P): Promise<R> {
        return this.callParseServerWithLoginHandler<R>(() => {
            return Parse.Cloud.run(fn, params)
        })
    }

    private async callParseServerWithLoginHandler<T>(fn: () => Promise<T>): Promise<T> {
        try {
            const response = await fn()
            return response
        } catch (err) {
            if (err.code !== 209) {
                throw err
            }

            const loginDialogResponse = await this.invalidSessionService.openLoginDialog()

            if (loginDialogResponse) {
                return this.callParseServerWithLoginHandler(fn)
            } else {
                throw err
            }
        }
    }

    public async getMailingTemplates(establishmentId: string): Promise<Objects.MailingTemplate.Parse[]> {
        if (!establishmentId) {
            return []
        }

        const establishment: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        const COLLECTION: Objects.MailingTemplate.Collection = 'MailingTemplate'
        const mailingTemplates = await new Parse.Query<Objects.MailingTemplate.Parse>(COLLECTION)
            .equalTo('establishment', establishment)
            .notEqualTo('isDeleted', true)
            .notEqualTo('isDefaultForCarrierRouteCheck', true)
            .ascending('name')
            .limit(Number.MAX_SAFE_INTEGER)
            .find()

        return mailingTemplates
    }

    public reportUIWarning(error: Error | { message: Error; stack: string }, fn: string, args: string) {
        let message = ''
        let stack = ''

        if (typeof error.message === 'object') {
            message = error.message.message
            stack = error.message.stack || ''
        } else {
            message = error.message
            stack = error.stack || ''
        }

        const params: Params.Error.Report = {
            message,
            fn,
            stack,
            args,
        }

        if (!this.environmentService.production) {
            console.error('UI Warning', params)
        }

        const functionName: FunctionNames.Warning.Report = 'reportWarning'
        Parse.Cloud.run(functionName, params).catch(() => undefined)
    }

    async updateEstablishmentIndustry(
        industryList: string[],
        isIndustrySelectionForced: boolean,
        isIndustrySelectionFromSignUp: boolean,
        establishmentId: string
    ) {
        const params: Params.Establishment.UpdateEstablishmentIndustry = {
            establishmentId,
            industryList,
            isIndustrySelectionForced,
            isIndustrySelectionFromSignUp,
        }
        const fn: FunctionNames.Establishment.UpdateEstablishmentIndustry = 'updateEstablishmentIndustry'
        const response: Responses.Establishment.UpdateEstablishmentIndustry = await Parse.Cloud.run(fn, params)

        return response
    }

    public reportUIError(error: Error | { message: Error; stack: string }, fn: string, args: string): void {
        let message = ''
        let stack = ''

        if (typeof error.message === 'object') {
            message = error.message.message
            stack = error.message.stack || ''
        } else {
            message = error.message
            stack = error.stack || ''
        }

        const params: Params.Error.Report = {
            message,
            fn,
            stack,
            args,
        }

        if (!this.environmentService.production) {
            console.error('UI Error', params)
        }

        const functionName: FunctionNames.Error.Report = 'reportError'
        Parse.Cloud.run(functionName, params).catch(() => undefined)
        this.sentry.handleError(error)
    }

    // query(collection: Objects.Job.Collection): Parse.Query<Objects.Job.Parse<{}>>
    query(collection: Objects.Mailing.Collection): Parse.Query<Objects.Mailing._Parse>
    query(collection: Objects.MailingList.Collection): Parse.Query<Objects.MailingList.Parse>
    query(collection: Objects.MailingListOrder.Collection): Parse.Query<Objects.MailingListOrder.Fetched.Any>
    query(collection: Objects.MailingOrder.Collection): Parse.Query<Objects.MailingOrder.Parse>
    query(collection: string) {
        return new Parse.Query(collection)
    }

    public async logIn(username: string, password: string, opts: { updateDefaultEstablishment: boolean }) {
        try {
            this.roleService.clearRoles()
            const user = (await Parse.User.logIn(username.trim().toLowerCase(), password.trim())) as Objects.User._Parse

            if (!user.get('isDeleted')) {
                this.user.next({
                    id: user.id,
                    firstName: user.get('firstName'),
                    lastName: user.get('lastName'),
                    username: user.get('username'),
                    phone: user.get('phone') ?? '',
                    createdAt: user.createdAt,
                })
            } else {
                throw new Error('User Account Disabled')
            }

            if (opts.updateDefaultEstablishment) {
                await this.updateDefaultSelectedEstablishment(user.id)
            }

            return user
        } catch (err) {
            const ignoreReportingErrorsList = ['Invalid username/password.']
            if (ignoreReportingErrorsList.indexOf(err?.message) === -1) {
                this.reportUIError(err, 'Parse.Service.logIn()', JSON.stringify({ username }))
            }
            throw err
        }
    }

    async updateDefaultSelectedEstablishment(userID: string) {
        try {
            const isWPOpsMember = await this.roleService.doesUserHavePermission('WP_Ops Member')
            const establishment = await this.establishmentService.getDefaultEstablishment(isWPOpsMember ? undefined : userID)
            this.selectedEstablishment.next(establishment)
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.updateDefaultSelectedEstablishment', '')
        }
    }

    public async logOut() {
        try {
            await Parse.User.logOut()
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.logOut()', '')
        }

        this.user.next(undefined)
    }

    public getErrorMessage(error: Error | string | { message: Error }) {
        if (typeof error === 'string') {
            return error
        }

        const { message } = error

        if (typeof message === 'object') {
            return message.message
        }

        return message
    }

    public async createMailingList(clientId: string): Promise<Parse.Object> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.MailingList.Create = {
            establishmentId: establishment.id,
            clientId,
        }
        const fn: FunctionNames.MailingList.Create = 'createMailingList'
        const response: Responses.MailingList.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    public async createSample(establishmentId: string) {
        const params: Params.Sample.Create = {
            establishmentId,
        }
        const fn: FunctionNames.Sample.Create = 'createSample'
        const response: Responses.Sample.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    public async addAddressToMailingList(mailingListId: string, establishmentId: string, address: { header: string; value: string }[]) {
        const params: Params.MailingList.AddAddress = {
            establishmentId,
            mailingListId,
            address,
        }
        const fn: FunctionNames.MailingList.AddAddress = 'addAddressToMailingList'
        const response: Responses.MailingList.AddAddress = await Parse.Cloud.run(fn, params)

        return response
    }

    public async getMailingListComparisonReport(mailingId: string): Promise<MailingComparisonReport> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.GetMailingListComparisonReport = {
            establishmentId: establishment.id,
            mailingId,
        }
        const fn: FunctionNames.GetMailingListComparisonReport = 'getMailingListComparisonReport'
        const response: Responses.GetMailingListComparisonReport = await Parse.Cloud.run(fn, params)

        return response
    }

    public getJobByMailingListOrderIdQuery(mailingListOrderId: string) {
        if (!mailingListOrderId) {
            return
        }

        const COLLECTION: Objects.Job.Collection = 'Job'
        const type: Objects.Job.ByType.MailingListOrder.Fulfill.Type = 'MAILING LIST ORDER - FULFILL'

        return new Parse.Query<Objects.Job.Parse<Objects.Job.ByType.MailingListOrder.Fulfill.Params, Objects.Job.ByType.MailingListOrder.Fulfill.Data>>(
            COLLECTION
        )
            .equalTo('type', type)
            .equalTo('params.mailingListOrderId', mailingListOrderId)
    }

    public async getUserRoles(userId: string, establishmentId: string) {
        const params: Params.User.GetRoles = {
            userId,
            establishmentId,
        }
        const fn: FunctionNames.User.GetRoles = 'getUserRoles'
        const response: Responses.User.GetRoles = await Parse.Cloud.run(fn, params)

        return response
    }

    public async getFileStorageSignedUrlForPrivatePut(
        collectionFieldCombination:
            | Objects.FileStorage.CollectionFieldCombination.MailingList.Original
            | Objects.FileStorage.CollectionFieldCombination.SuppressionList.Original
            | Objects.FileStorage.CollectionFieldCombination.MailingListDataFile.Original
            | Objects.FileStorage.CollectionFieldCombination.Mailing.CutStackUrl
    ) {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const params: Params.FileStorage.GetSignedUrlForPrivatePut = {
            establishmentId: establishment.id,
            collectionFieldCombination,
        }
        const fn: FunctionNames.FileStorage.GetSignedUrlForPrivatePut = 'getFileStorageSignedUrlForPrivatePut'
        const response: Responses.FileStorage.GetSignedUrlForPrivatePut = await Parse.Cloud.run(fn, params)

        return response
    }

    public async getFileStorageSignedUrlForPrivateGet(params: Params.FileStorage.GetSignedUrlForPrivateGet) {
        const fn: FunctionNames.FileStorage.GetSignedUrlForPrivateGet = 'getFileStorageSignedUrlForPrivateGet'
        const response: Responses.FileStorage.GetSignedUrlForPrivateGet = await Parse.Cloud.run(fn, params)

        return response
    }

    public async getFileStorageSignedUrlForAdminOnlyGet(params: Params.FileStorage.GetSignedUrlForAdminOnlyGet) {
        const fn: FunctionNames.FileStorage.GetSignedUrlForAdminOnlyGet = 'getFileStorageSignedUrlForAdminOnlyGet'
        const response: Responses.FileStorage.GetSignedUrlForAdminOnlyGet = await Parse.Cloud.run(fn, params)

        return response
    }

    public async updateMailingList(mailingListId: string, clientId: string, name: string, records: number, originalCSV: string): Promise<Parse.Object> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const params: Params.MailingList.Update = {
            establishmentId: establishment.id,
            mailingListId,
            clientId,
            name,
            records,
            originalCSV,
        }
        const fn: FunctionNames.MailingList.Update = 'updateMailingList'
        const response: Responses.MailingList.Update = await Parse.Cloud.run(fn, params)
        return response
    }

    public async updateMailingListDataFile(
        establishmentId: string,
        mailingListDataFileId: string,
        clientId: string,
        originalCSV: string
    ): Promise<Parse.Object> {
        const params: Params.MailingListDataFile.Update = {
            establishmentId,
            mailingListDataFileId,
            clientId,
            originalCSV,
        }
        const fn: FunctionNames.MailingListDataFile.Update = 'updateMailingListDataFile'
        const response: Responses.MailingListDataFile.Update = await Parse.Cloud.run(fn, params)

        return response
    }

    public async replaceMailingList(
        newMailingListId: string,
        oldMailingListId: string,
        clientId: string,
        records: number,
        originalCSV: string
    ): Promise<Parse.Object> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.MailingList.Replace = {
            establishmentId: establishment.id,
            newMailingListId,
            oldMailingListId,
            clientId,
            records,
            originalCSV,
        }
        const fn: FunctionNames.MailingList.Replace = 'replaceMailingList'
        const response: Responses.MailingList.Replace = await Parse.Cloud.run(fn, params)

        return response
    }

    public async getMailingList(id: string) {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const COLLECTION: Objects.MailingList.Collection = 'MailingList'
            const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

            const query = this.createParseQuery(COLLECTION)
                .equalTo('objectId', id)
                .equalTo('establishment', this.createParseObject(ESTABLISHMENT_COLLECTION, establishment.id))
                .equalTo('isDeleted', false)

            const mailingList = await query.first()
            return mailingList
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getMailingList()', JSON.stringify({ id }))
        }
    }
    public async getMailingListOrder(id: string) {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const COLLECTION: Objects.MailingListOrder.Collection = 'MailingListOrder'
            const establishmentFilter = this.createParseObject('Establishment', establishment.id)

            const query = this.query(COLLECTION).equalTo('objectId', id).equalTo('establishment', establishmentFilter).include(['client', 'mailingList'])
            const result = await query.first()
            return result
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getMailingList()', JSON.stringify({ id }))
        }
    }

    public getMailingListOrdersQuery() {
        const COLLECTION: Objects.MailingListOrder.Collection = 'MailingListOrder'
        return new Parse.Query<Objects.MailingListOrder.Fetched.Any>(COLLECTION).include('establishment')
    }

    public getMailingsQuery(clientId: string) {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const client = new Parse.User()
        client.id = clientId

        const COLLECTION: Objects.Mailing.Collection = 'Mailing'
        const establishmentFilter: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishment.id,
        }

        return new Parse.Query<Objects.Mailing._Parse>(COLLECTION)
            .equalTo('establishment', establishmentFilter)
            .equalTo('client', client)
            .equalTo('isDeleted', false)
    }

    public async getSuppressionList(clientId: string): Promise<Parse.Object[]> {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const collection: Objects.SuppressionList.Collection = 'SuppressionList'

            const establishmentPtr: Objects.Establishment.ParsePointer = {
                __type: 'Pointer',
                className: 'Establishment',
                objectId: establishment.id,
            }

            const clientPtr: Objects.User.ParsePointer = {
                __type: 'Pointer',
                className: '_User',
                objectId: clientId,
            }

            const query = new Parse.Query<Objects.SuppressionList.Parse>(collection)
                .equalTo('establishment', establishmentPtr)
                .equalTo('client', clientPtr)
                .equalTo('isDeleted', false)

            const suppressionList = await query.find()
            return suppressionList
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getSuppressionList()', '')
        }

        return []
    }

    public async findMailingListDataFiles(establishmentId: string, clientId: string): Promise<Parse.Object[]> {
        try {
            const client = new Parse.User()
            client.id = clientId

            const collection: Objects.MailingListDataFile.Collection = 'MailingListDataFile'
            const FIELDS: Objects.MailingListDataFile.Fields = {
                client: 'client',
                establishment: 'establishment',
                isDeleted: 'isDeleted',
                name: 'name',
                original: 'original',
            }
            const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'
            const establishmentFilter = new Parse.Object(ESTABLISHMENT_COLLECTION)
            establishmentFilter.id = establishmentId

            const query: Parse.Query = new Parse.Query(collection)
                .equalTo(FIELDS.establishment, establishmentFilter)
                .equalTo(FIELDS.client, client)
                .equalTo(FIELDS.isDeleted, false)

            const dataFiles: Parse.Object[] = await query.find()
            return dataFiles
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.findMailingListDataFiles()', '')
            throw err
        }
    }

    public async findUpdateableMailingLists(clientId: string) {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const client = new Parse.User()
            client.id = clientId

            const COLLECTION: Objects.MailingList.Collection = 'MailingList'
            const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

            const allListTypes: Objects.MailingList.ListType.All = ['ASV-SCAN-LIST', 'ASV-TOOL-LEAD-LIST', 'MY-ADDRESS-LIST', 'STANDARD']
            const updateableListTypes = allListTypes.filter((listType) => listType === 'STANDARD')

            const query = this.createParseQuery(COLLECTION)
                .equalTo('establishment', this.createParseObject(ESTABLISHMENT_COLLECTION, establishment.id))
                .equalTo('client', client)
                .equalTo('isDeleted', false)
                .exists('mapped')
                .containedIn('listType', updateableListTypes)
                .ascending('name')
                .limit(Number.MAX_SAFE_INTEGER)

            const mailingLists = await query.find()
            return mailingLists
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.findUpdateableMailingLists()', JSON.stringify({ clientId }))
        }

        return []
    }

    public findMailableMailingListsQuery(clientId: string) {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const client = new Parse.User()
        client.id = clientId

        const COLLECTION: Objects.MailingList.Collection = 'MailingList'
        const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

        return this.createParseQuery(COLLECTION)
            .equalTo('establishment', this.createParseObject(ESTABLISHMENT_COLLECTION, establishment.id))
            .equalTo('client', client)
            .equalTo('isDeleted', false)
            .greaterThan('records', 0)
            .exists('mapped')
            .ascending('name')
            .limit(Number.MAX_SAFE_INTEGER)
    }

    public async findMailableMailingLists(clientId: string): Promise<Objects.MailingList.Parse[]> {
        try {
            const query = this.findMailableMailingListsQuery(clientId)

            const mailingLists = await query.find()
            return mailingLists
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.findMailableMailingLists()', JSON.stringify({ clientId }))
        }

        return []
    }

    public async findUnMappedMailingLists(clientId: string) {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const client = new Parse.User()
            client.id = clientId

            const COLLECTION: Objects.MailingList.Collection = 'MailingList'
            const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

            const allListTypes: Objects.MailingList.ListType.All = ['ASV-SCAN-LIST', 'ASV-TOOL-LEAD-LIST', 'MY-ADDRESS-LIST', 'STANDARD']
            const validUnMappedListTypes = allListTypes.filter((listType) => listType === 'STANDARD')

            const query = this.createParseQuery(COLLECTION)
                .equalTo('establishment', this.createParseObject(ESTABLISHMENT_COLLECTION, establishment.id))
                .equalTo('client', client)
                .doesNotExist('mapped')
                .equalTo('isDeleted', false)
                .containedIn('listType', validUnMappedListTypes)
                .descending('createdAt')
                .limit(Number.MAX_SAFE_INTEGER)

            const mailingLists = await query.find()
            return mailingLists
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.findUnMappedMailingLists()', JSON.stringify({ clientId }))
        }

        return []
    }

    public async deleteMailingList(id: string): Promise<true> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const params: Params.MailingList.Delete = {
            establishmentId: establishment.id,
            id,
        }
        const fn: FunctionNames.MailingList.Delete = 'deleteMailingList'
        const response: Responses.MailingList.Delete = await Parse.Cloud.run(fn, params)

        return response
    }

    public async deleteMailing(mailingId: string): Promise<true> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.Mailing.Delete = {
            establishmentId: establishment.id,
            mailingId,
        }
        const fn: FunctionNames.Mailing.Delete = 'deleteMailing'
        const response: Responses.Mailing.Delete = await Parse.Cloud.run(fn, params)

        return response
    }

    public async mapMailingList(mailingListId: string, map: string[]): Promise<Responses.MailingList.Map> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const params: Params.MailingList.Map = {
            establishmentId: establishment.id,
            id: mailingListId,
            map,
        }
        const fn: FunctionNames.MailingList.Map = 'mapMailingList'
        const response: Responses.MailingList.Map = await Parse.Cloud.run(fn, params)
        return response
    }

    public async getMailingLists(clientId: string) {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const client = new Parse.User()
            client.id = clientId

            const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

            const COLLECTION: Objects.MailingList.Collection = 'MailingList'
            const query = this.createParseQuery(COLLECTION, ['job'])
                .equalTo('establishment', this.createParseObject(ESTABLISHMENT_COLLECTION, establishment.id))
                .equalTo('client', client)
                .equalTo('isDeleted', false)
                .descending('createdAt')
                .limit(Number.MAX_SAFE_INTEGER)

            const mailingLists = await query.find()
            return mailingLists
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getMailingLists()', JSON.stringify({ clientId }))
        }

        return []
    }

    public async getMappedMailingLists(clientId: string): Promise<Objects.MailingList.Parse[]> {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const client = new Parse.User()
            client.id = clientId

            const COLLECTION: Objects.MailingList.Collection = 'MailingList'
            const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

            const query = this.createParseQuery(COLLECTION)
                .equalTo('establishment', this.createParseObject(ESTABLISHMENT_COLLECTION, establishment.id))
                .equalTo('client', client)
                .equalTo('isDeleted', false)
                .exists('mapped')
                .ascending('name')
                .limit(Number.MAX_SAFE_INTEGER)

            const mailingLists = await query.find()
            return mailingLists
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getMappedMailingListsByClient()', JSON.stringify({ clientId }))
        }

        return []
    }

    public async createMailingListDataFile(establishmentId: string, clientId: string, name: string): Promise<Parse.Object> {
        const params: Params.MailingListDataFile.Create = {
            establishmentId,
            clientId,
            name,
        }

        const fn: FunctionNames.MailingListDataFile.Create = 'createMailingListDataFile'
        const response: Responses.MailingListDataFile.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    public async createPrintAndShipMailingPDF(mailingId: string) {
        const params: Params.Mailing.CreatePrintAndShipPDF = {
            mailingId,
        }

        const fn: FunctionNames.Mailing.CreatePrintAndShipPDF = 'createPrintAndShipMailingPDF'
        const response: Responses.Mailing.CreatePrintAndShipPDF = await Parse.Cloud.run(fn, params)

        return response
    }

    public async sendMailingToPostalOne(mailingId: string) {
        const params: Params.Mailing.SendToPostalOne = {
            mailingId,
        }

        const fn: FunctionNames.Mailing.SendToPostalOne = 'sendMailingToPostalOne'
        const response: Responses.Mailing.SendToPostalOne = await Parse.Cloud.run(fn, params)

        return response
    }

    public async markPrintAndShipMailingAsShipped(mailingId: string, carrier: string, trackingNumber: string) {
        const params: Params.Mailing.MarkPrintAndShipAsShipped = {
            carrier,
            mailingId,
            trackingNumber,
        }

        const fn: FunctionNames.Mailing.MarkPrintAndShipAsShipped = 'markPrintAndShipMailingAsShipped'
        const response: Responses.Mailing.MarkPrintAndShipAsShipped = await Parse.Cloud.run(fn, params)

        return response
    }

    public async markInHouseMailingAsManuallyCompleted(mailingId: string) {
        const params: Params.Mailing.MarkInHouseAsManuallyCompleted = {
            mailingId,
        }

        const fn: FunctionNames.Mailing.MarkInHouseAsManuallyCompleted = 'markInHouseMailingAsManuallyCompleted'
        const response: Responses.Mailing.MarkInHouseAsManuallyCompleted = await Parse.Cloud.run(fn, params)

        return response
    }

    public async createSuppressionList(clientId: string): Promise<Parse.Object> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const params: Params.CreateSuppressionList = {
            establishmentId: establishment.id,
            clientId,
        }

        const fn: FunctionNames.CreateSuppressionList = 'createSuppressionList'
        const response: Responses.CreateSuppressionList = await Parse.Cloud.run(fn, params)

        return response
    }

    public async updateSuppressionList(suppressionListId: string, clientId: string, records: number, originalCSV: string): Promise<Parse.Object> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const params: Params.UpdateSuppressionList = {
            establishmentId: establishment.id,
            suppressionListId,
            clientId,
            records,
            originalCSV,
        }

        const fn: FunctionNames.UpdateSuppressionList = 'updateSuppressionList'
        const response: Responses.UpdateSuppressionList = await Parse.Cloud.run(fn, params)

        return response
    }

    public findTrackedMailingsQuery(establishmentID: string, clientID: string) {
        const COLLECTION: Objects.Mailing.Collection = 'Mailing'

        const establishmentFilter = this.createParseObject('Establishment', establishmentID)

        const client = new Parse.User()
        client.id = clientID

        return new Parse.Query<Objects.Mailing._Parse>(COLLECTION)
            .equalTo('establishment', establishmentFilter)
            .equalTo('client', client)
            .equalTo('isComplete', true)
            .equalTo('isDeleted', false)
            .select('name', 'mailedAt', 'options', 'records', 'establishment', 'postageClass', 'printAndShipTrackingNumber')
    }

    public findTrackedMailingOrdersQuery(establishmentID: string, clientID: string) {
        const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'

        const establishmentFilter: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentID,
        }

        const client = new Parse.User() as Objects.User._Parse
        client.id = clientID
        return new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
            .equalTo('establishment', establishmentFilter)
            .equalTo('client', client)
            .containedIn('status', ['Complete-L', 'Paid-Pending Mailing', 'Pending Payment', 'Submitted'])
    }

    public async getTrackingBreakdown(
        establishmentId: string,
        clientId: string,
        mailingOrMailingOrderId: string,
        trackingType: 'mailing' | 'mailingOrder'
    ): Promise<Responses.Tracking.GetBreakdownAsCustomer | Responses.Tracking.GetBreakdownAsClient | Responses.Tracking.GetBreakdownAsWPOps> {
        const [hasRoleWPOpsMember, hasRoleAdmin, hasRoleProFulfillmentManager] = await Promise.all([
            this.roleService.doesUserHavePermission('WP_Ops Member'),
            this.roleService.doesUserHavePermission('Admin'),
            this.roleService.doesUserHavePermission('ProFulfillment Manager'),
        ])

        if (hasRoleWPOpsMember) {
            const params: Params.Tracking.GetBreakdownAsWPOps = {
                mailingOrMailingOrderId,
                trackingType,
            }
            const fn: FunctionNames.Tracking.GetBreakdownAsWPOps = 'getTrackingBreakdownAsWPOps'
            const response: Responses.Tracking.GetBreakdownAsWPOps = await Parse.Cloud.run(fn, params)
            return response
        } else if (hasRoleAdmin || hasRoleProFulfillmentManager) {
            const params: Params.Tracking.GetBreakdownAsClient = {
                establishmentId,
                clientId,
                mailingOrMailingOrderId,
                trackingType,
            }
            const fn: FunctionNames.Tracking.GetBreakdownAsClient = 'getTrackingBreakdownAsClient'
            const response: Responses.Tracking.GetBreakdownAsClient = await Parse.Cloud.run(fn, params)
            return response
        } else {
            const params: Params.Tracking.GetBreakdownAsCustomer = {
                establishmentId,
                clientId,
                mailingOrMailingOrderId,
                trackingType,
            }
            const fn: FunctionNames.Tracking.GetBreakdownAsCustomer = 'getTrackingBreakdownAsCustomer'
            const response: Responses.Tracking.GetBreakdownAsCustomer = await Parse.Cloud.run(fn, params)

            return response
        }
    }

    public async getTrackingRowsByZip(
        establishmentId: string,
        clientId: string,
        mailingOrMailingOrderId: string,
        trackingType: 'mailing' | 'mailingOrder',
        zip: number
    ): Promise<Responses.Tracking.GetRowsByZipAsClient | Responses.Tracking.GetRowsByZipAsCustomer | Responses.Tracking.GetRowsByZipAsWPOps> {
        const [hasRoleWPOpsMember, hasRoleAdmin, hasRoleProFulfillmentManager] = await Promise.all([
            this.roleService.doesUserHavePermission('WP_Ops Member'),
            this.roleService.doesUserHavePermission('Admin'),
            this.roleService.doesUserHavePermission('ProFulfillment Manager'),
        ])

        if (hasRoleWPOpsMember) {
            const params: Params.Tracking.GetRowsByZipAsWPOps = {
                mailingOrMailingOrderId,
                trackingType,
                zip,
            }

            const fn: FunctionNames.Tracking.GetRowsByZipAsWPOps = 'getTrackingRowsByZipAsWPOps'
            const response: Responses.Tracking.GetRowsByZipAsWPOps = await Parse.Cloud.run(fn, params)

            return response
        } else if (hasRoleAdmin || hasRoleProFulfillmentManager) {
            const params: Params.Tracking.GetRowsByZipAsClient = {
                establishmentId,
                clientId,
                mailingOrMailingOrderId,
                trackingType,
                zip,
            }

            const fn: FunctionNames.Tracking.GetRowsByZipAsClient = 'getTrackingRowsByZipAsClient'
            const response: Responses.Tracking.GetRowsByZipAsClient = await Parse.Cloud.run(fn, params)

            return response
        } else {
            const params: Params.Tracking.GetRowsByZipAsCustomer = {
                establishmentId,
                clientId,
                mailingOrMailingOrderId,
                trackingType,
                zip,
            }

            const fn: FunctionNames.Tracking.GetRowsByZipAsCustomer = 'getTrackingRowsByZipAsCustomer'
            const response: Responses.Tracking.GetRowsByZipAsCustomer = await Parse.Cloud.run(fn, params)

            return response
        }
    }

    public async getTrackingEventsURL(establishmentId: string, trackingRowID: string) {
        const [hasRoleWPOpsMember, hasRoleAdmin, hasRoleProFulfillmentManager] = await Promise.all([
            this.roleService.doesUserHavePermission('WP_Ops Member'),
            this.roleService.doesUserHavePermission('Admin'),
            this.roleService.doesUserHavePermission('ProFulfillment Manager'),
        ])

        if (hasRoleWPOpsMember) {
            const params: Params.Tracking.GetEventsUrlAsWPOps = {
                establishmentId,
                trackingRowID,
            }

            const fn: FunctionNames.Tracking.GetEventsUrlAsWPOps = 'getTrackingEventsUrlAsWPOps'
            const response: Responses.Tracking.GetEventsUrlAsWPOps = await Parse.Cloud.run(fn, params)

            return response
        } else if (hasRoleProFulfillmentManager || hasRoleAdmin) {
            const params: Params.Tracking.GetEventsUrlAsClient = {
                establishmentId,
                trackingRowID,
            }

            const fn: FunctionNames.Tracking.GetEventsUrlAsClient = 'getTrackingEventsUrlAsClient'
            const response: Responses.Tracking.GetEventsUrlAsClient = await Parse.Cloud.run(fn, params)

            return response
        } else {
            const params: Params.Tracking.GetEventsUrlAsCustomer = {
                establishmentId,
                trackingRowID,
            }

            const fn: FunctionNames.Tracking.GetEventsUrlAsCustomer = 'getTrackingEventsUrlAsCustomer'
            const response: Responses.Tracking.GetEventsUrlAsCustomer = await Parse.Cloud.run(fn, params)

            return response
        }
    }

    public async getTrackingEvents(
        establishmentId: string,
        imb: string,
        trayEventsParams: {
            mailingId: string
            containerId: string | undefined
        },
        validationParams: {
            trackingRowId: string
            mailingOrMailingOrderId: string
            clientId: string
            trackingType: 'mailing' | 'mailingOrder'
        }
    ): Promise<Responses.TrackingEvent.GetEventsAsClient | Responses.TrackingEvent.GetEventsAsCustomer | Responses.TrackingEvent.GetEventsAsWPOps> {
        const [hasRoleWPOpsMember, hasRoleAdmin, hasRoleProFulfillmentManager] = await Promise.all([
            this.roleService.doesUserHavePermission('WP_Ops Member'),
            this.roleService.doesUserHavePermission('Admin'),
            this.roleService.doesUserHavePermission('ProFulfillment Manager'),
        ])

        if (hasRoleWPOpsMember) {
            const params: Params.TrackingEvent.GetEventsAsWPOps = {
                imb,
                trayEventsParams,
                establishmentId,
            }

            const fn: FunctionNames.TrackingEvent.GetEventsAsWPOps = 'getTrackingEventsAsWPOps'
            const response: Responses.TrackingEvent.GetEventsAsWPOps = await Parse.Cloud.run(fn, params)

            return response
        } else if (hasRoleAdmin || hasRoleProFulfillmentManager) {
            const params: Params.TrackingEvent.GetEventsAsClient = {
                imb,
                trayEventsParams,
                establishmentId,
                validationParams,
            }

            const fn: FunctionNames.TrackingEvent.GetEventsAsClient = 'getTrackingEventsAsClient'
            const response: Responses.TrackingEvent.GetEventsAsClient = await Parse.Cloud.run(fn, params)

            return response
        } else {
            const params: Params.TrackingEvent.GetEventsAsCustomer = {
                imb,
                trayEventsParams,
                establishmentId,
                validationParams,
            }

            const fn: FunctionNames.TrackingEvent.GetEventsAsCustomer = 'getTrackingEventsAsCustomer'
            const response: Responses.TrackingEvent.GetEventsAsCustomer = await Parse.Cloud.run(fn, params)

            return response
        }
    }

    public async createOrUpdateMailingTemplate(name: string, options: Objects.MailingTemplate.Fields.Options.Value) {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.CreateOrUpdateMailingTemplate = {
            establishmentId: establishment.id,
            name,
            options,
        }

        const fn: FunctionNames.CreateOrUpdateMailingTemplate = 'createOrUpdateMailingTemplate'
        const response: Responses.CreateOrUpdateMailingTemplate = await Parse.Cloud.run(fn, params)

        return response
    }

    public async signUpSubCustomer(firstName: string, lastName: string, password: string, username: string, externalReference: string | undefined) {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.SignUpSubCustomer = {
            establishmentId: establishment.id,
            firstName,
            lastName,
            password,
            username,
            externalReference,
        }
        const fn: FunctionNames.SignUpSubCustomer = 'signUpSubCustomer'
        const response: Responses.SignUpSubCustomer = await Parse.Cloud.run(fn, params)
        return response
    }

    public async signUpProFulfillmentCustomer(firstName: string, lastName: string, password: string, username: string, externalReference: string | undefined) {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.User.SignUp.ProFulfillmentCustomer = {
            establishmentId: establishment.id,
            firstName,
            lastName,
            password,
            username,
            externalReference,
        }
        const fn: FunctionNames.User.SignUp.ProFulfillmentCustomer = 'signUpProFulfillmentCustomer'
        const response: Responses.User.SignUp.ProFulfillmentCustomer = await Parse.Cloud.run(fn, params)
        return response
    }

    public async signUpProFulfillmentPayer(firstName: string, lastName: string, username: string, password: string, phone: string) {
        const params: Params.User.SignUp.ProFulfillmentPayer = {
            firstName,
            lastName,
            username,
            password,
            phone,
            referrerUserId: undefined,
            referralUserId: undefined,
        }

        const fn: FunctionNames.User.SignUp.ProFulfillmentPayer = 'signUpProFulfillmentPayer'
        const response: Responses.User.SignUp.ProFulfillmentPayer = await Parse.Cloud.run(fn, params)

        return response
    }

    public async runMailingListAgainstSuppressionList(mailingListId: string, clientId: string) {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }
        const params: Params.MailingList.RunAgainstSuppressionList = {
            establishmentId: establishment.id,
            clientId,
            id: mailingListId,
        }

        const fn: FunctionNames.MailingList.RunAgainstSuppressionList = 'runMailingListAgainstSuppressionList'
        const response: Responses.MailingList.RunAgainstSuppressionList = await Parse.Cloud.run(fn, params)

        return response
    }

    public async getMailingOrderDesignMailingVariables(
        establishmentId: string,
        mailingOrderId: string
    ): Promise<Responses.MailingOrder.GetDesignMailingVariables> {
        const params: Params.MailingOrder.GetDesignMailingVariables = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrder.GetDesignMailingVariables = 'getMailingOrderDesignMailingVariables'
        const response: Responses.MailingOrder.GetDesignMailingVariables = await Parse.Cloud.run(fn, params)
        return response
    }

    public async getResetPasswordTokenValidity(token: string) {
        const params: Params.User.GetResetPasswordTokenValidity = {
            token,
        }
        const fn: FunctionNames.User.GetResetPasswordTokenValidity = 'getResetPasswordTokenValidity'
        const response: Responses.User.GetResetPasswordTokenValidity = await Parse.Cloud.run(fn, params)
        return response
    }

    public async updatePassword(password: string, token: string) {
        const params: Params.User.UpdateUserPassword = {
            token,
            password,
        }
        const fn: FunctionNames.User.UpdateUserPassword = 'updateUserPassword'
        const response: Responses.User.UpdateUserPassword = await Parse.Cloud.run(fn, params)
        return response
    }

    public async requestPasswordReset(email: string) {
        const params: Params.User.RequestPasswordReset = {
            email,
        }
        const fn: FunctionNames.User.RequestPasswordReset = 'requestPasswordReset'
        const response: Responses.User.RequestPasswordReset = await Parse.Cloud.run(fn, params)
        return response
    }

    public async createMailingOrder(
        establishmentId: string,
        draftMailingOrderId: string,
        clientId: string,
        mailingListsConfig: Objects.MailingOrder.IFields.MailingListsConfig.Value,
        mailingOrderName: string,
        postageClass: Objects.MailingOrder.IFields.PostageClass.Value,
        asvOptions: Objects.MailingOrder.IFields.AutomatedSellerValuationOptions.Value | undefined,
        earliestMailingDate: Date,
        shouldUseCredits: boolean,
        paper: Objects.Mailing.IFields.Paper,
        printCoating: Objects.Mailing.IFields.PrintCoating,
        paymentDetails: Extract<
            Objects.Job.ByType.MailingOrder.Create.PaymentDetails,
            | {
                  type: 'Stripe'
                  paymentMethodId: string
                  isDeferred: boolean
              }
            | {
                  type: 'Freshbooks'
              }
        >,
        returnAddress: Objects.MailingOrder.IFields.ReturnAddress.Value | undefined,
        printAndShipOpts: Objects.MailingOrder.PrintAndShipOptions | undefined,
        estimatedDeliveryDate: string | undefined,
        discountCode: string | undefined
    ) {
        const params: Params.MailingOrder.Create = {
            asvOptions,
            clientId,
            earliestMailingDate,
            establishmentId,
            fieldHeaderMapping: undefined,
            mailingListsConfig,
            mailingListId: undefined,
            draftMailingOrderId,
            mailingOrderName,
            postageClass,
            shouldUseCredits,
            paper,
            printCoating,
            paymentDetails,
            returnAddress,
            printAndShipOpts,
            estimatedDeliveryDate,
            discountCode,
        }

        const fn: FunctionNames.MailingOrder.Create = 'createMailingOrder'
        const response: Responses.MailingOrder.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    public async freshBooksTokens(code: string) {
        const params: Params.FreshBooksTokens = {
            code,
        }
        const fn: FunctionNames.FreshBooksTokens = 'freshBooksTokens'
        const response: Responses.FreshBooksTokens = await Parse.Cloud.run(fn, params)
        return response
    }
    public async refreshFreshBooksTokens(freshBookId: string) {
        const params: Params.RefreshFreshBooksTokens = {
            freshBookId,
        }
        const fn: FunctionNames.RefreshFreshBooksTokens = 'refreshFreshBooksTokens'
        const response: Responses.RefreshFreshBooksTokens = await Parse.Cloud.run(fn, params)

        return response
    }

    public async getPdfServerState() {
        const params: Params.PDFServer.GetState = undefined
        const fn: FunctionNames.PDFServer.GetState = 'getPdfServerState'
        const response: Responses.PDFServer.GetState = await Parse.Cloud.run(fn, params)
        return response
    }
    public async startPdfServer() {
        const params: Params.PDFServer.Start = undefined
        const fn: FunctionNames.PDFServer.Start = 'startPdfServer'
        const response: Responses.PDFServer.Start = await Parse.Cloud.run(fn, params)
        return response
    }
    public async stopPdfServer() {
        const params: Params.PDFServer.Stop = undefined
        const fn: FunctionNames.PDFServer.Stop = 'stopPdfServer'
        const response: Responses.PDFServer.Stop = await Parse.Cloud.run(fn, params)
        return response
    }

    public getOpenMailingOrders(): Parse.Query<Objects.MailingOrder.Parse> {
        const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'
        // const STATUS_PAID_PENDING_MAILING: Objects.MailingOrder.IFields.Status.Value = 'Paid-Pending Mailing';

        return (
            new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
                .equalTo('status', 'Paid-Pending Mailing')
                // .containedIn('status', [STATUS_PAID_PENDING_MAILING])
                .equalTo('isDeleted', false)
                .include(['client', 'establishment'])
        )
    }

    public async getFreshBooksTokens() {
        try {
            const COLLECTION: Objects.FreshBooks.Collection = 'FreshBooks'
            const query = new Parse.Query(COLLECTION)
            const object: Parse.Object | undefined = await query.first()

            if (object) {
                const tokens = {
                    objectId: object.id,
                    accessToken: object.get('accessToken') || '',
                    refreshToken: object.get('refreshToken') || '',
                    scope: object.get('scope') || '',
                }
                return tokens
            }
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getFreshBooksTokens()', '')
        }
    }

    public getFulfilledMailingOrdersQuery(): Parse.Query<Objects.MailingOrder.Parse> {
        const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'
        return new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
            .equalTo('status', 'Complete-L')
            .equalTo('isDeleted', false)
            .include(['client', 'establishment'])
    }

    public async updatePdfPreviewsForMailingOrder(
        establishmentId: string,
        mailingOrderId: string,
        back: boolean,
        front: boolean,
        page3: boolean,
        page4: boolean
    ) {
        const params: Params.MailingOrder.UpdatePdfPreviews = {
            establishmentId,
            mailingOrderId,
            back,
            front,
            page3,
            page4,
        }

        const fn: FunctionNames.MailingOrder.UpdatePdfPreviews = 'updateMailingOrderPdfPreviews'
        const response: Responses.MailingOrder.UpdatePdfPreviews = await Parse.Cloud.run(fn, params)

        return response
    }

    public async copyMailingOrderDesignIntoNewDesignTemplate(establishmentId: string, mailingOrderId: string, templateName: string) {
        const params: Params.MailingOrder.CopyDesignIntoNewDesignTemplate = {
            establishmentId,
            mailingOrderId,
            templateName,
        }
        const fn: FunctionNames.MailingOrder.CopyDesignIntoNewDesignTemplate = 'copyMailingOrderDesignIntoNewDesignTemplate'
        const response = await this.callCloudWithLoginHandler<Responses.MailingOrder.CopyDesignIntoNewDesignTemplate>(fn, params)

        return response
    }

    // TODO Check where this is used and fix
    public async getMailingOrders(clientId: string) {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const establishmentFilter = this.createParseObject('Establishment', establishment.id)

            const user = (await new Parse.Query<Objects.User._Parse.ForEstablishmentQuery>(Parse.User)
                .equalTo('objectId', clientId)
                .equalTo('establishment', establishmentFilter)
                .first()) as unknown as Objects.User._Parse | undefined

            if (!user) {
                return []
            } else {
                const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'
                const STATUS_COMPLETE_L: Objects.MailingOrder.IFields.Status.Value = 'Complete-L'

                const mailingOrders = await new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
                    .equalTo('establishment', establishmentFilter)
                    .equalTo('client', user)
                    .equalTo('isDeleted', false)
                    .equalTo('status', STATUS_COMPLETE_L)
                    .find()

                return mailingOrders
            }
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getAllMailingOrder()', '')
        }

        return []
    }

    public async updateOrCreateSimpleEditorAssets(changes: Params.SimpleEditorAsset.UpdateOrCreate['changes']) {
        const establishment = this.selectedEstablishment.value
        if (!establishment) {
            return
        }
        const params: Params.SimpleEditorAsset.UpdateOrCreate = {
            changes,
            establishmentId: establishment.id,
        }
        const fn: FunctionNames.SimpleEditorAsset.UpdateOrCreate = 'updateOrCreateSimpleEditorAssets'
        const response = await this.callCloudWithLoginHandler<Responses.SimpleEditorAsset.UpdateOrCreate2>(fn, params)
        return response
    }

    public async saveMailingOrderDesign(
        establishmentId: string,
        mailingOrderId: string,
        type: 'CHILI',
        designXml: string,
        editorType: Objects.ChiliConfig.EditorType
    ) {
        const params: Params.MailingOrder.SaveDesign = {
            establishmentId,
            mailingOrderId,
            type,
            xml: designXml,
            editorType,
        }

        const fn: FunctionNames.MailingOrder.SaveDesign = 'saveMailingOrderDesign'
        const response = await this.callCloudWithLoginHandler<Responses.MailingOrder.SaveDesign>(fn, params)

        return response
    }

    public async saveMailingOrderDesignAsAdmin(
        establishmentId: string,
        mailingOrderId: string,
        type: 'CHILI',
        designXml: string,
        editorType: Objects.ChiliConfig.EditorType
    ) {
        const params: Params.MailingOrder.SaveDesignAsAdmin = {
            establishmentId,
            mailingOrderId,
            type,
            xml: designXml,
            editorType,
        }

        const fn: FunctionNames.MailingOrder.SaveDesignAsAdmin = 'saveMailingOrderDesignAsAdmin'
        const response = await this.callCloudWithLoginHandler<Responses.MailingOrder.SaveDesignAsAdmin>(fn, params)

        return response
    }

    public async saveMailingOrderAsDraft(
        mailingOrderId: string,
        mailingOrderName: string | undefined,
        clientId: string | undefined,
        mailingListIds: string[],
        earliestMailingDate: Date,
        postageClass: Objects.MailingOrder.IFields.PostageClass.Value,
        paper: Objects.Mailing.IFields.Paper,
        printCoating: Objects.Mailing.IFields.PrintCoating,
        establishmentId: string,
        selectedAssetsBySectionId: Record<string, undefined | string> | undefined, // loaded section
        returnAddress: Objects.MailingOrder.IFields.ReturnAddress.Value | undefined,
        asvMapping: Objects.MailingOrder.IFields.AutomatedSellerValuationOptions.AsvMapping | undefined,
        firstListMapping: RecordMap<string> | undefined,
        printAndShipOpts: Objects.MailingOrder.PrintAndShipOptions | undefined
    ) {
        const params: Params.MailingOrder.SaveDraft = {
            earliestMailingDate,
            establishmentId,
            mailingOrderId,
            mailingOrderName,
            clientId,
            mailingListIds,
            paper,
            postageClass,
            printCoating,
            selectedAssetsBySectionId,
            returnAddress,
            asvMapping,
            firstListMapping,
            printAndShipOpts,
        }

        const fn: FunctionNames.MailingOrder.SaveDraft = 'saveMailingOrderAsDraft'
        const response = await this.callCloudWithLoginHandler<Responses.MailingOrder.SaveDraft>(fn, params)

        return response
    }

    public async renameMailingOrder(mailingOrderId: string, establishmentId: string, mailingOrderName: string) {
        const params: Params.MailingOrder.RenameDraft = {
            establishmentId,
            mailingOrderId,
            mailingOrderName,
        }

        const fn: FunctionNames.MailingOrder.RenameDraft = 'renameMailingOrder'
        const response = await this.callCloudWithLoginHandler<Responses.MailingOrder.RenameDraft>(fn, params)

        return response
    }

    public async loadCSV(establishmentId: string, collectionFieldCombination: Objects.FileStorage.CollectionFieldCombination.Private.Get): Promise<Csv> {
        const urlToLoad = await this.getFileStorageSignedUrlForPrivateGet({ establishmentId, collectionFieldCombination })

        if (!urlToLoad) {
            throw new Error('No Url found')
        }

        const file = await firstValueFrom(this.http.get(urlToLoad, { responseType: 'text' }))
        const loadedCSV = new Csv(collectionFieldCombination.objectId, file, urlToLoad)
        // console.log('[ Mailing.Manage.ViewUpdatedButtonComponent ] - loadList() - complete');

        return loadedCSV
    }

    public getUnpaidMailingOrdersQuery() {
        const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'
        const STATUS_PENDING_PAYMENT: Objects.MailingOrder.IFields.Status.Value = 'Pending Payment'

        return new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
            .equalTo('status', STATUS_PENDING_PAYMENT)
            .equalTo('isDeleted', false)
            .include(['client', 'establishment'])
    }

    public async webhooksList() {
        const params: Params.FreshBooksWebhookList = {
            allWebhook: true,
        }
        const fn: FunctionNames.FreshBooksWebhookList = 'freshBooksWebhookList'
        const response: Responses.FreshBooksWebhookList = await Parse.Cloud.run(fn, params)
        return response
    }

    public async deleteFreshbookWebhook(callbackId: string) {
        if (!callbackId) {
            throw new Error('Selected row Id is missing')
        }
        const params: Params.FreshBookDeleteWebHook = {
            callbackId,
        }
        const fn: FunctionNames.FreshBookDeleteWebHook = 'freshBookDeleteWebHook'
        const response: Responses.FreshBookDeleteWebHook = await Parse.Cloud.run(fn, params)
        return response
    }

    public async createFreshbookWebhook(event: string, uri: string) {
        if (!event) {
            throw new Error('Please provide event for webhook')
        }

        if (!uri) {
            throw new Error('Please Provide a endpoint for webhook')
        }

        const params: Params.FreshBookCreateWebHook = {
            event,
            uri,
        }
        const fn: FunctionNames.FreshBookCreateWebHook = 'freshBookCreateWebHook'
        const response: Responses.FreshBookCreateWebHook = await Parse.Cloud.run(fn, params)
        return response
    }

    public async enqueueMailingMailablePDF(
        establishmentId: string,
        mailingId: string,
        sortDirection: 'asc' | 'des',
        cutAndStack: boolean,
        itemsPerPage: Objects.Mailing.IFields.MailablePdfCreationParams.Fields.ItemsPerPage,
        focusedMailingOrderId: string | undefined,
        dieCutter: boolean
    ) {
        const params: Params.Mailing.EnqueueMailablePdf = {
            establishmentId,
            mailingId,
            cutAndStack,
            sortDirection,
            itemsPerPage,
            focusedMailingOrderId,
            dieCutter,
        }

        const fn: FunctionNames.Mailing.EnqueueMailablePdf = 'enqueueMailingMailablePDF'
        const response: Responses.Mailing.EnqueueMailablePdf = await Parse.Cloud.run(fn, params)
        return response
    }

    public async dequeueMailingMailablePDF(establishmentId: string, mailingId: string) {
        if (!establishmentId) {
            throw new Error('No Establishment Found')
        }
        const params: Params.Mailing.DequeueMailablePdf = {
            establishmentId,
            mailingId,
        }

        const fn: FunctionNames.Mailing.DequeueMailablePdf = 'dequeueMailingMailablePDF'
        const response: Responses.Mailing.DequeueMailablePdf = await Parse.Cloud.run(fn, params)
        return response
    }

    public async createPreviewPdfWithDataForMailingOrder(
        establishmentId: string,
        mailingOrderId: string,
        fulfillmentMethod: Objects.Mailing.FulfillmentMethod
    ) {
        const params: Params.MailingOrder.CreatePdfPreviewWithData = {
            establishmentId,
            fulfillmentMethod,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrder.CreatePdfPreviewWithData = 'createMailingOrderPdfPreviewWithData'
        const response: Responses.MailingOrder.CreatePdfPreviewWithData = await Parse.Cloud.run(fn, params)
        return response
    }

    // Not for Clients and Customers, would need input validation on server
    public async createPreviewPdfWithDataForMultipleMailingOrders(mailingOrderIdList: string[], fulfillmentMethod: Objects.Mailing.FulfillmentMethod) {
        const params: Params.MailingOrder.CreateMultiplePdfPreviewsWithData = {
            fulfillmentMethod,
            mailingOrderIdList,
        }
        const fn: FunctionNames.MailingOrder.CreateMultiplePdfPreviewsWithData = 'createMultipleMailingOrderPdfPreviewsWithData'
        const response: Responses.MailingOrder.CreateMultiplePdfPreviewsWithData = await Parse.Cloud.run(fn, params)
        return response
    }

    public async sendTemplateAsEmail(establishmentId: string, mailingOrderId: string, email: string) {
        const client = await this.getEstablishmentPayer(establishmentId)
        if (!client) {
            throw new Error('Unable to find Client')
        }

        const replyTo = client.getUsername()
        if (!replyTo) {
            throw new Error('Unable to find client Email')
        }

        const params: Params.MailingOrder.SendTemplateAsEmail = {
            establishmentId,
            mailingOrderId,
            email,
            replyTo,
        }

        const fn: FunctionNames.MailingOrder.SendTemplateAsEmail = 'sendMailingOrderTemplateAsEmail'
        const response = await this.callCloudWithLoginHandler<Responses.MailingOrder.SendTemplateAsEmail>(fn, params)
        return response
    }

    public async downloadMailingOrderProof(establishmentId: string, mailingOrderId: string) {
        const params: Params.MailingOrder.DownloadProof = {
            establishmentId,
            mailingOrderId,
        }

        const fn: FunctionNames.MailingOrder.DownloadProof = 'downloadMailingOrderProof'
        const response = await this.callCloudWithLoginHandler<Responses.MailingOrder.DownloadProof>(fn, params)
        return response
    }

    public async copyMailingOrderIntoNewDraft(establishmentId: string, mailingOrderId: string) {
        const params: Params.MailingOrder.CopyIntoNewDraft = {
            establishmentId,
            mailingOrderId,
        }

        const fn: FunctionNames.MailingOrder.CopyIntoNewDraft = 'copyMailingOrderIntoNewDraft'
        const response: Responses.MailingOrder.CopyIntoNewDraft = await Parse.Cloud.run(fn, params)
        return response
    }

    public async editMailingOrders(mailingOrderIDs: string[], changes: Params.MailingOrder.EditPaidPendingMailing['changes'], runDesignQA: boolean) {
        const params: Params.MailingOrder.EditPaidPendingMailing = {
            mailingOrderIDs,
            changes,
            runDesignQA,
        }

        const fn: FunctionNames.MailingOrder.EditPaidPendingMailing = 'editMailingOrdersPaidPendingMailing'
        const response: Responses.MailingOrder.EditPaidPendingMailing = await Parse.Cloud.run(fn, params)
        return response
    }

    public getSavedDraftOrdersQuery(establishmentId: string) {
        const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'

        const establishmentFilter: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        return new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
            .equalTo('establishment', establishmentFilter)
            .equalTo('status', 'saved-draft')
            .equalTo('isDeleted', false)
    }

    public async getMailingsByStatus(clientId: string, status: Objects.MailingOrder.IFields.Status.Value): Promise<Parse.Object[]> {
        try {
            const establishment = this.selectedEstablishment.getValue()
            if (!establishment) {
                throw new Error('No Establishment selected')
            }

            const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'
            const establishmentFilter = new Parse.Object(ESTABLISHMENT_COLLECTION)
            establishmentFilter.id = establishment.id

            const user = (await new Parse.Query<Objects.User._Parse.ForEstablishmentQuery>(Parse.User)
                .equalTo('objectId', clientId)
                .equalTo('establishment', establishmentFilter)
                .first()) as unknown as Objects.User._Parse | undefined

            if (!user) {
                return []
            } else {
                const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'
                const establishmentPointer: Objects.Establishment.ParsePointer = {
                    __type: 'Pointer',
                    className: 'Establishment',
                    objectId: establishmentFilter.id,
                }

                const mailingOrders = await new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
                    .equalTo('establishment', establishmentPointer)
                    .equalTo('client', user)
                    .equalTo('status', status)
                    .equalTo('isDeleted', false)
                    .find()

                return mailingOrders
            }
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getMailingsByStatus()', '')
        }

        return []
    }

    public async deleteDraftMailingOrder(establishmentId: string, mailingOrderId: string): Promise<true> {
        const params: Params.MailingOrder.DeleteDraft = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrder.DeleteDraft = 'deleteDraftMailingOrder'
        const response: Responses.MailingOrder.DeleteDraft = await Parse.Cloud.run(fn, params)

        return response
    }

    public async deleteCompleteMailingOrder(establishmentId: string, mailingOrderId: string): Promise<true> {
        const params: Params.MailingOrder.DeleteComplete = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrder.DeleteComplete = 'deleteCompleteMailingOrder'
        const response: Responses.MailingOrder.DeleteComplete = await Parse.Cloud.run(fn, params)

        return response
    }

    public async findMailingOrdersById(ids: string[]) {
        const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'
        const response = await new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
            .containedIn('objectId', ids)
            .equalTo('isDeleted', false)
            .limit(ids.length)
            .find()

        return response
    }

    public async getMailing(mailingId: string) {
        const COLLECTION: Objects.Mailing.Collection = 'Mailing'
        const mailing = await new Parse.Query<Objects.Mailing._Parse>(COLLECTION).equalTo('objectId', mailingId).equalTo('isDeleted', false).first()

        return mailing
    }

    public async getMailingOrder(mailingOrderId: string) {
        try {
            const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'
            const response = await new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION)
                .equalTo('objectId', mailingOrderId)
                .equalTo('isDeleted', false)
                .include('establishment', 'client', 'chiliDocument')
                .first()

            return response
        } catch (err) {
            this.reportUIError(err, 'Parse.Service.getMailingOrder()', '')
        }
    }

    // TODO Rename and also return the email
    public async getEstablishmentPayer(establishmentId: string): Promise<Objects.User._Parse | undefined> {
        const client = await this.callParseServerWithLoginHandler(() => {
            const collection: Objects.Establishment.Collection = 'Establishment'
            const establishmentFilter = new Parse.Object(collection)
            establishmentFilter.id = establishmentId

            return new Parse.Query<Objects.User._Parse.ForEstablishmentQuery>(Parse.User)
                .equalTo('establishment', establishmentFilter)
                .ascending('createdAt')
                .first()
        })

        return client as Objects.User._Parse | undefined
    }

    public async getEstablishment(establishmentId: string): Promise<Objects.Establishment.Parse | undefined> {
        const collection: Objects.Establishment.Collection = 'Establishment'
        return new Parse.Query<Objects.Establishment.Parse>(collection).equalTo('objectId', establishmentId).first()
    }

    public async getEstablishmentProfile(establishmentId: string): Promise<Objects.EstablishmentProfile.Parse | undefined> {
        const ESTABLISHMENT_PROFILE_COLLECTION: Objects.EstablishmentProfile.Collection = 'EstablishmentProfile'
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }
        const establishmentProfile = await new Parse.Query<Objects.EstablishmentProfile.Parse>(ESTABLISHMENT_PROFILE_COLLECTION)
            .equalTo('establishment', establishmentPointer)
            .include('headshot', 'companyLogo', 'brokerageLogo')
            .first()
        return establishmentProfile
    }

    public async getImageColors(imageId: string, establishmentId: string) {
        const params: Params.Image.GetColors = {
            imageId,
            establishmentId,
        }
        const fn: FunctionNames.Image.GetColors = 'getImageColors'
        const response: Responses.Image.GetColors = await Parse.Cloud.run(fn, params)

        return response
    }

    public getPreviousMailingsQuery(): Parse.Query<Objects.MailingOrder.Parse> {
        const COLLECTION: Objects.MailingOrder.Collection = 'MailingOrder'

        const statusComplete: Objects.MailingOrder.IFields.Status.Value = 'Complete-L'
        const statusSubmitted: Objects.MailingOrder.IFields.Status.Value = 'Submitted'
        const statusPaidPendingMailing: Objects.MailingOrder.IFields.Status.Value = 'Paid-Pending Mailing'
        const statusPendingPayment: Objects.MailingOrder.IFields.Status.Value = 'Pending Payment'

        const query = new Parse.Query<Objects.MailingOrder.Parse>(COLLECTION).equalTo('isDeleted', false)

        query.containedIn('status', [statusComplete, statusSubmitted, statusPaidPendingMailing, statusPendingPayment]).descending('createdAt')
        query.include('designTemplate')
        return query
    }

    public async disableCustomer(userId: string): Promise<true> {
        const establishment = this.selectedEstablishment.getValue()
        if (!establishment) {
            throw new Error('No Establishment selected')
        }

        const params: Params.DisableCustomer = {
            userId,
            establishmentId: establishment.id,
        }
        const fn: FunctionNames.DisableCustomer = 'disableCustomer'
        const response: Responses.DisableCustomer = await Parse.Cloud.run(fn, params)

        return response
    }

    public async signUpExternalClient(
        firstName: string,
        lastName: string,
        username: string,
        password: string,
        phone: string,
        referrerUserId: string | undefined,
        utm: Objects.Establishment.UtmParameters | undefined,
        industryAtSignUp: string | undefined,
        signUpTags: string[] | undefined,
        allUtm: {
            login: Objects.Establishment.UtmParameters
            referral: Objects.Establishment.UtmParameters
            query: Objects.Establishment.UtmParameters
            cookie: Objects.Establishment.UtmParameters
        },
        allCjEvents: {
            query: string | undefined
            cookie: string | undefined
        }
    ) {
        this.roleService.clearRoles()
        const params: Params.User.SignUp.ExternalClient = {
            firstName,
            industryAtSignUp,
            lastName,
            username,
            password,
            phone,
            referrerUserId,
            referralUserId: referrerUserId,
            utm,
            signUpTags,
            allUtm,
            allCjEvents,
        }
        const fn: FunctionNames.User.SignUp.ExternalClient = 'signupExternalClient'
        const response: Responses.User.SignUp.ExternalClient = await Parse.Cloud.run(fn, params)

        return response
    }

    /** Used if payment is through invoice. */
    public async createMailingListOrderByAddressAndRadius(
        establishmentId: string,
        clientId: string,
        city: string,
        addressCount: number,
        mailingListName: string,
        radius: number,
        state: string,
        street: string,
        propertyTypeOptions: Objects.PropertyData.PropertyTypeOptions,
        source: NonNullable<Objects.PropertyData.Source>,
        advancedFilters: Objects.PropertyData.V3.FindWithPolygonV3RequestData.AdvancedFilters | undefined,
        coordinate: { lat: number; lon: number }
    ) {
        const params: Params.MailingListOrder.Create.ByAddressAndRadius = {
            city,
            clientId,
            establishmentId,
            expectedAddressCount: addressCount,
            mailingListName,
            propertyTypeOptions,
            radius,
            state,
            street,
            source,
            advancedFilters,
            coordinate,
        }
        const fn: FunctionNames.MailingListOrder.Create.ByAddressAndRadius = 'createMailingListOrderByAddressAndRadius'
        const response: Responses.MailingListOrder.Create.ByAddressAndRadius = await Parse.Cloud.run(fn, params)
        return response
    }

    public async createMailingListOrderByAddressAndRadiusWithinPolygon(
        establishmentId: string,
        clientId: string,
        city: string,
        addressCount: number,
        mailingListName: string,
        radius: number,
        state: string,
        street: string,
        coordinates: { lat: number; lon: number }[],
        propertyTypeOptions: Objects.PropertyData.PropertyTypeOptions,
        source: Objects.PropertyData.Source,
        advancedFilters: Objects.PropertyData.V3.FindWithPolygonV3RequestData.AdvancedFilters | undefined
    ) {
        const params: Params.MailingListOrder.Create.ByAddressAndRadiusWithinPolygon = {
            city,
            clientId,
            coordinates,
            establishmentId,
            expectedAddressCount: addressCount,
            mailingListName,
            propertyTypeOptions,
            radius,
            state,
            street,
            source,
            advancedFilters,
        }
        const fn: FunctionNames.MailingListOrder.Create.ByAddressAndRadiusWithinPolygon = 'createMailingListOrderByAddressAndRadiusWithinPolygon'
        const response: Responses.MailingListOrder.Create.ByAddressAndRadiusWithinPolygon = await Parse.Cloud.run(fn, params)
        return response
    }

    public async createMailingListOrderByGeoId(
        establishmentId: string,
        clientId: string,
        addressCount: number,
        mailingListName: string,
        city: string,
        geoIdV4: string,
        state: string,
        subdivision: string,
        propertyTypeOptions: Objects.PropertyData.PropertyTypeOptions
    ) {
        const params: Params.MailingListOrder.Create.ByGeoId = {
            city,
            clientId,
            establishmentId,
            expectedAddressCount: addressCount,
            geoIdV4,
            mailingListName,
            propertyTypeOptions,
            state,
            subdivision,
            source: 'attom',
        }
        const fn: FunctionNames.MailingListOrder.Create.ByGeoId = 'createMailingListOrderByGeoId'
        const response: Responses.MailingListOrder.Create.ByGeoId = await Parse.Cloud.run(fn, params)
        return response
    }

    public async updateMailingListOrder(establishmentId: string, mailingListOrderId: string, addressCount: number) {
        const params: Params.MailingListOrder.Update = {
            establishmentId,
            addressCount,
            mailingListOrderId,
        }
        const fn: FunctionNames.MailingListOrder.Update = 'updateMailingListOrder'
        const response: Responses.MailingListOrder.Update = await Parse.Cloud.run(fn, params)
        return response
    }

    public async getRelatedMailingOrders(mailing: Objects.Mailing._Parse, include?: (keyof Objects.MailingOrder.Attributes)[]) {
        if (!mailing) {
            return []
        }

        return mailing
            .relation<Objects.MailingOrder.Parse, 'mailingOrders'>('mailingOrders')
            .query()
            .include(include || [])
            .limit(Number.MAX_SAFE_INTEGER)
            .find()
    }

    // Should be similar to server-side equivalent
    public awaitJobCompletion<P extends object, D>(job: Objects.Job.Parse<P, D>, intervalMs: number) {
        return new Promise<Objects.Job.Parse<P, D>>((resolve, reject) => {
            setTimeout(async () => {
                try {
                    const collection: Objects.Job.Collection = 'Job'

                    const fetchedJob = await this.callParseServerWithLoginHandler(() => {
                        return new Parse.Query<Objects.Job.Parse<P, D>>(collection).equalTo('objectId', job.id).first()
                    })

                    if (!fetchedJob) {
                        return reject(new Error('No Job found'))
                    }

                    const message = fetchedJob.get('message')
                    const hasError = fetchedJob.get('hasError')
                    if (hasError) {
                        return reject(new Error(message))
                    }

                    const progress = fetchedJob.get('progress')
                    if (progress === 100) {
                        return resolve(fetchedJob)
                    }
                    resolve(this.awaitJobCompletion(job, intervalMs + 100))
                } catch (err) {
                    reject(err)
                }
            }, intervalMs)
        })
    }

    async getEstablishmentCredit(establishmentId: string) {
        const params: Params.Establishment.GetCredit = {
            establishmentId,
        }
        const fn: FunctionNames.Establishment.GetCredit = 'getEstablishmentCredit'
        const response: Responses.Establishment.GetCredit = await Parse.Cloud.run(fn, params)

        return response
    }

    async getEstablishmentPrices(establishmentId: string) {
        const params: Params.Establishment.GetPrices = {
            establishmentId,
        }
        const fn: FunctionNames.Establishment.GetPrices = 'getEstablishmentPrices'
        const response: Responses.Establishment.GetPrices = await Parse.Cloud.run(fn, params)

        return response
    }

    async updateEstablishmentMlsProfiles(establishmentId: string, mlsProfiles: Params.Establishment.UpdateMlsProfiles['mlsProfiles']) {
        const params: Params.Establishment.UpdateMlsProfiles = {
            establishmentId,
            mlsProfiles,
        }
        const fn: FunctionNames.Establishment.UpdateMlsProfiles = 'updateEstablishmentMlsProfiles'
        const response: Responses.Establishment.UpdateMlsProfiles = await Parse.Cloud.run(fn, params)

        return response
    }

    async searchEstablishments(filter: string) {
        const collection: Objects.Establishment.Collection = 'Establishment'

        return Parse.Query.or(
            new Parse.Query<Objects.Establishment.Parse>(collection).matches('name', new RegExp(filter, 'i')), // case insensitive,

            new Parse.Query<Objects.Establishment.Parse>(collection).matches('objectId', new RegExp(filter, 'i'))
        )
            .notEqualTo('isDeleted', true)
            .addAscending(['name'])
            .limit(50)
            .find()
    }

    async retrievePaymentIntent(paymentIntentId: string) {
        const params: Params.Stripe.PaymentIntent.Retrieve = {
            paymentIntentId,
        }

        const fn: FunctionNames.Stripe.PaymentIntent.Retrieve = 'retrieveMyPaymentIntent'
        const response: Responses.Stripe.PaymentIntent.Retrieve = await Parse.Cloud.run(fn, params)

        return response
    }

    async createOrUpdatePaymentIntentForMailingListOrderByAddressAndRadius(
        establishmentId: string,
        city: string,
        clientId: string,
        addressCount: number,
        mailingListName: string,
        radius: number,
        state: string,
        street: string,
        paymentIntentId: string | undefined,
        propertyTypeOptions: Objects.PropertyData.PropertyTypeOptions,
        source: NonNullable<Objects.PropertyData.Source>,
        advancedFilters: Objects.PropertyData.V3.FindWithPolygonV3RequestData.AdvancedFilters | undefined,
        coordinate: { lat: number; lon: number } | undefined
    ) {
        const params: Params.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByAddressAndRadius = {
            establishmentId,
            city,
            clientId,
            expectedAddressCount: addressCount,
            mailingListName,
            radius,
            state,
            street,
            paymentIntentId,
            propertyTypeOptions,
            source,
            advancedFilters,
            coordinate,
        }

        const fn: FunctionNames.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByAddressAndRadius =
            'createOrUpdatePaymentIntentForMailingListOrderByAddressAndRadius'
        const response: Responses.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByAddressAndRadius = await Parse.Cloud.run(fn, params)

        return response
    }

    async createOrUpdatePaymentIntentForMailingListOrderByGeoId(
        establishmentId: string,
        clientId: string,
        addressCount: number,
        mailingListName: string,
        city: string,
        geoIdV4: string,
        state: string,
        subdivision: string,
        paymentIntentId: string | undefined,
        propertyTypeOptions: Objects.PropertyData.PropertyTypeOptions
    ) {
        const params: Params.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByGeoId = {
            establishmentId,
            clientId,
            expectedAddressCount: addressCount,
            geoIdV4,
            city,
            mailingListName,
            paymentIntentId,
            propertyTypeOptions,
            state,
            subdivision,
            source: 'attom',
        }

        const fn: FunctionNames.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByGeoId = 'createOrUpdatePaymentIntentForMailingListOrderByGeoId'
        const response: Responses.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByGeoId = await Parse.Cloud.run(fn, params)

        return response
    }

    /**
     *
     * Parse Service to call whenever the user is on the 2nd step of list purchasing
     * to get an updated Stripe payment intent
     */
    async createOrUpdatePaymentIntentForMailingListOrderByAddressAndRadiusWithinPolygon(
        establishmentId: string,
        city: string,
        clientId: string,
        addressCount: number,
        mailingListName: string,
        radius: number,
        state: string,
        street: string,
        coordinates: { lat: number; lon: number }[],
        paymentIntentId: string | undefined,
        propertyTypeOptions: Objects.PropertyData.PropertyTypeOptions,
        source: NonNullable<Objects.PropertyData.Source>,
        advancedFilters: Objects.PropertyData.V3.FindWithPolygonV3RequestData.AdvancedFilters | undefined
    ) {
        const params: Params.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByAddressAndRadiusWithinPolygon = {
            establishmentId,
            city,
            clientId,
            coordinates,
            expectedAddressCount: addressCount,
            mailingListName,
            radius,
            state,
            street,
            paymentIntentId,
            propertyTypeOptions,
            source,
            advancedFilters,
        }

        const fn: FunctionNames.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByAddressAndRadiusWithinPolygon =
            'createOrUpdatePaymentIntentForMailingListOrderByAddressAndRadiusWithinPolygon'
        const response: Responses.Stripe.PaymentIntent.CreateOrUpdate.MailingListOrder.ByAddressAndRadiusWithinPolygon = await Parse.Cloud.run(fn, params)

        return response
    }

    async createPaymentIntentForMailingListOrderUpdate(establishmentId: string, mailingListOrderId: string, addressCount: number) {
        const fn: FunctionNames.Stripe.PaymentIntent.Create.MailingListOrder.ForUpdate = 'createPaymentIntentForMailingListOrderUpdate'
        const params: Params.Stripe.PaymentIntent.Create.MailingListOrder.ForUpdate = {
            establishmentId,
            addressCount,
            mailingListOrderId,
        }
        const response: Responses.Stripe.PaymentIntent.Create.MailingListOrder.ForUpdate = await Parse.Cloud.run(fn, params)

        return response
    }

    async createOrUpdatePaymentIntentForMailingOrder(
        asvOptions: Objects.MailingOrder.IFields.AutomatedSellerValuationOptions.Value | undefined,
        clientId: string,
        draftMailingOrderId: string,
        earliestMailingDate: Date,
        establishmentId: string,
        frontEndPriceCents: number,
        mailingListsConfig: Objects.MailingOrder.IFields.MailingListsConfig.Value,
        mailingOrderName: string,
        paymentIntentId: string | undefined,
        postageClass: Objects.MailingOrder.IFields.PostageClass.Value,
        shouldUseCredits: boolean,
        paper: Objects.Mailing.IFields.Paper,
        printCoating: Objects.Mailing.IFields.PrintCoating,
        returnAddress: Objects.MailingOrder.IFields.ReturnAddress.Value | undefined,
        printAndShipOpts: Objects.MailingOrder.PrintAndShipOptions | undefined,
        estimatedDeliveryDate: string | undefined,
        discountCode: string | undefined
    ) {
        const params: Params.Stripe.PaymentIntent.CreateOrUpdate.MailingOrder.New = {
            asvOptions,
            clientId,
            draftMailingOrderId,
            earliestMailingDate,
            establishmentId,
            fieldHeaderMapping: undefined,
            frontEndPriceCents,
            mailingListsConfig,
            mailingListId: undefined,
            mailingOrderName,
            paper,
            paymentIntentId,
            postageClass,
            printCoating,
            shouldUseCredits,
            returnAddress,
            printAndShipOpts,
            estimatedDeliveryDate,
            discountCode,
        }
        const fn: FunctionNames.Stripe.PaymentIntent.CreateOrUpdate.MailingOrder.New = 'createOrUpdatePaymentIntentForMailingOrder'
        const response: Responses.Stripe.PaymentIntent.CreateOrUpdate.MailingOrder.New = await Parse.Cloud.run(fn, params)

        return response
    }

    async createOrUpdatePaymentIntentForSubsequentMailingOrder(
        establishmentId: string,
        frontEndPriceCents: number,
        paymentIntentId: string | undefined,
        mailCount: number,
        mailInterval: number,
        originalPaymentIntentId: string,
        isOriginalPaymentIntentDeferred: boolean
    ) {
        const params: Params.Stripe.PaymentIntent.CreateOrUpdate.MailingOrder.Subsequent = {
            establishmentId,
            frontEndPriceCents,
            paymentIntentId,
            mailCount,
            mailInterval,
            originalPaymentIntentId,
            isOriginalPaymentIntentDeferred,
        }

        const fn: FunctionNames.Stripe.PaymentIntent.CreateOrUpdate.MailingOrder.Subsequent = 'createOrUpdatePaymentIntentForSubsequentMailingOrder'
        const response: Responses.Stripe.PaymentIntent.CreateOrUpdate.MailingOrder.Subsequent = await Parse.Cloud.run(fn, params)

        return response
    }

    async updateMyProfile(firstName: string, lastName: string, phone: string, email: string) {
        const params: Params.User.UpdateMyProfile = {
            firstName,
            lastName,
            phone,
            email,
        }
        const fn: FunctionNames.User.UpdateMyProfile = 'updateMyProfile'
        const response: Responses.User.UpdateMyProfile = await Parse.Cloud.run(fn, params)

        return response
    }

    async updateEstablishmentProfile(establishmentId: string, fields: Objects.EstablishmentProfile.Fields) {
        const params: Params.EstablishmentProfile.UpdateEstablishmentProfile = {
            establishmentId,
            fields,
        }
        const fn: FunctionNames.EstablishmentProfile.UpdateEstablishmentProfile = 'updateEstablishmentProfile'
        const response: Responses.EstablishmentProfile.UpdateEstablishmentProfile = await Parse.Cloud.run(fn, params)

        return response
    }

    async createOnboardingForm(fields: Objects.OnboardingForm.Form.Fields, establishmentId: string) {
        const params: Params.OnboardingForm.Create = {
            establishmentId,
            fields,
        }

        const fn: FunctionNames.OnboardingForm.Create = 'createOnboardingForm'
        const response: Responses.OnboardingForm.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    async createOrUpdateOnboardingStatus(
        establishmentId: string,
        isCompleted: boolean,
        step: Objects.OnboardingStatus.Attributes.Step,
        saveCompletionDate: boolean
    ) {
        const params: Params.OnboardingStatus.CreateOrUpdate = {
            establishmentId,
            isCompleted,
            step,
            saveCompletionDate,
        }
        const fn: FunctionNames.OnboardingStatus.CreateOrUpdate = 'createOrUpdateOnboardingStatus'
        const response: Responses.OnboardingStatus.CreateOrUpdate = await Parse.Cloud.run(fn, params)

        return response
    }

    async changeMyPassword(oldPassword: string, newPassword: string) {
        const params: Params.User.ChangeMyPassword = {
            newPassword,
            oldPassword,
        }
        const fn: FunctionNames.User.ChangeMyPassword = 'changeMyPassword'
        const response: Responses.User.ChangeMyPassword = await Parse.Cloud.run(fn, params)

        return response
    }

    async getMyEstablishment() {
        const currentUser = Parse.User.current() as Objects.User._Parse
        if (!currentUser) {
            return
        }

        await currentUser.fetch()

        const establishment = await currentUser.relation<Objects.Establishment.Parse, 'establishment'>('establishment').query().first()
        return establishment
    }

    async getCensusPlacesWithinGeoId(geoIdV4: string, establishmentId: string) {
        const params: Params.AttomGeoIdLookup.GetCensusPlacesWithinGeoId = {
            establishmentId,
            geoIdV4,
        }
        const fn: FunctionNames.AttomGeoIdLookup.GetCensusPlacesWithinGeoId = 'getCensusPlacesWithinGeoId'
        const response: Responses.AttomGeoIdLookup.GetCensusPlacesWithinGeoId = await Parse.Cloud.run(fn, params)

        return response
    }

    async isValidHomeValuationKey(key: string): Promise<boolean> {
        const params: Params.HomeValuation.IsValidKey = {
            key,
        }
        const fn: FunctionNames.HomeValuation.IsValidKey = 'isValidHomeValuationKey'
        const response: Responses.HomeValuation.IsValidKey = await Parse.Cloud.run(fn, params)

        return response
    }

    async sendHomeValuationEmail(email: string, homeValuationId: string, key: string) {
        const params: Params.HomeValuation.SendEmail = {
            email,
            homeValuationId,
            key,
        }
        const fn: FunctionNames.HomeValuation.SendEmail = 'sendHomeValuationEmail'
        const response: Responses.HomeValuation.SendEmail = await Parse.Cloud.run(fn, params)
        return response
    }

    async createHomeValuationView(homeValuationKey: string) {
        const params: Params.HomeValuationView.Create = {
            homeValuationKey,
        }
        const fn: FunctionNames.HomeValuationView.Create = 'createHomeValuationView'
        const response: Responses.HomeValuationView.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    async getHomeValuationPropertyAddress(key: string) {
        const params: Params.HomeValuation.GetPropertyAddress = {
            key,
        }
        const fn: FunctionNames.HomeValuation.GetPropertyAddress = 'getHomeValuationPropertyAddress'
        const response: Responses.HomeValuation.GetPropertyAddress = await Parse.Cloud.run(fn, params)

        return response
    }

    async getHomeValuationPropertyValuationFromUrl(id: string) {
        const params: Params.HomeValuation.GetPropertyValuationFromUrl = {
            id,
        }
        const fn: FunctionNames.HomeValuation.GetPropertyValuationFromUrl = 'getHomeValuationPropertyValuationFromUrl'
        const response: Responses.HomeValuation.GetPropertyValuationFromUrl = await Parse.Cloud.run(fn, params)

        return response
    }

    async getMaponicsResidentialSubdivisionsWithinGeoId(geoIdV4: string, establishmentId: string) {
        const params: Params.AttomGeoIdLookup.GetMaponicsResidentialSubdivisionsWithinGeoId = {
            establishmentId,
            geoIdV4,
        }
        const fn: FunctionNames.AttomGeoIdLookup.GetMaponicsResidentialSubdivisionsWithinGeoId = 'getMaponicsResidentialSubdivisionsWithinGeoId'
        const response: Responses.AttomGeoIdLookup.GetMaponicsResidentialSubdivisionsWithinGeoId = await Parse.Cloud.run(fn, params)

        return response
    }

    async loadHomeValuationSummary(establishmentId: string, mailingOrderId: string) {
        const params: Params.HomeValuation.LoadSummary = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.HomeValuation.LoadSummary = 'loadHomeValuationSummary'
        const response: Responses.HomeValuation.LoadSummary = await Parse.Cloud.run(fn, params)

        return response
    }

    async getAddressFromCoordinates(polygon: { lat: number; lon: number }[]) {
        const bounds = new google.maps.LatLngBounds()
        for (const point of polygon) {
            bounds.extend(new google.maps.LatLng(point.lat, point.lon))
        }

        const center = bounds.getCenter()

        const params: Params.Utils.GetAddressFromCoordinate = {
            lat: center.lat(),
            lng: center.lng(),
        }
        const fn: FunctionNames.Utils.GetAddressFromCoordinate = 'getAddressFromCoordinate'
        const response: Responses.Utils.GetAddressFromCoordinate = await Parse.Cloud.run(fn, params)
        return response
    }

    async findEstablishmentPaymentMethodDetails(establishmentID: string) {
        const params: Params.Stripe.PaymentMethod.FindEstablishmentDetails = {
            establishmentID,
        }
        const fn: FunctionNames.Stripe.PaymentMethod.FindEstablishmentDetails = 'findEstablishmentPaymentMethodDetails'
        const response: Responses.Stripe.PaymentMethod.FindEstablishmentDetails = await Parse.Cloud.run(fn, params)
        return response
    }

    async createCheckoutSessionForDesignService(establishmentId: string, designServiceCode: Objects.DesignServiceProduct.Code) {
        const params: Params.Stripe.CheckoutSession.DesignService.Create = {
            establishmentId,
            designServiceCode,
        }

        const fn: FunctionNames.Stripe.CheckoutSession.DesignService.Create = 'createCheckoutSessionForDesignService'
        const response: Responses.Stripe.CheckoutSession.DesignService.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    async findMyPaymentMethodDetails() {
        const loggedInUser = this.user.getValue()
        if (!loggedInUser) {
            return []
        }
        const params: Params.Stripe.PaymentMethod.FindMyDetails = {}
        const fn: FunctionNames.Stripe.PaymentMethod.FindMyDetails = 'findMyPaymentMethodDetails'
        const response: Responses.Stripe.PaymentMethod.FindMyDetails = await Parse.Cloud.run(fn, params)
        return response
    }

    async getInvoices(establishmentID: string, limit?: number, starting_after?: string) {
        const params: Params.Stripe.Invoices.GetInvoices = { establishmentId: establishmentID, limit, starting_after }
        const fn: FunctionNames.Stripe.Invoices.GetInvoices = 'getInvoices'
        const response: Responses.Stripe.Invoices.GetInvoices = await Parse.Cloud.run(fn, params)
        return response
    }

    async createFacebookAudience(
        establishmentId: string,
        clientId: string,
        mailingListId: string,
        accessToken: string,
        fbAdActId: string,
        fbAudienceName: string
    ) {
        const params: Params.MailingList.CreateFacebookAudience = {
            establishmentId,
            clientId,
            mailingListId,
            accessToken,
            fbAdActId,
            fbAudienceName,
        }
        const fn: FunctionNames.MailingList.CreateFacebookAudience = 'createFacebookAudience'
        const response: Responses.MailingList.CreateFacebookAudience = await Parse.Cloud.run(fn, params)

        return response
    }

    async createFacebookAudienceAsAdmin(
        establishmentId: string,
        clientId: string,
        mailingListId: string,
        accessToken: string,
        fbAdActId: string,
        fbAudienceName: string
    ) {
        const params: Params.MailingList.CreateFacebookAudienceAsAdmin = {
            establishmentId,
            clientId,
            mailingListId,
            accessToken,
            fbAdActId,
            fbAudienceName,
        }
        const fn: FunctionNames.MailingList.CreateFacebookAudienceAsAdmin = 'createFacebookAudienceAsAdmin'
        const response: Responses.MailingList.CreateFacebookAudienceAsAdmin = await Parse.Cloud.run(fn, params)

        return response
    }

    async getChiliIdmlUrl(establishmentId: string, mailingOrderId: string) {
        const params: Params.MailingOrder.GetDesignIdmlURL = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrder.GetDesignIdmlURL = 'getMailingOrderDesignIdmlURL'
        const response: Responses.MailingOrder.GetDesignIdmlURL = await Parse.Cloud.run(fn, params)

        return response
    }

    async getMailingOrderPriceBreakdown(
        asvOptions: Objects.MailingOrder.IFields.AutomatedSellerValuationOptions.Value | undefined,
        establishmentId: string,
        mailingListIds: string[],
        postageClass: Objects.MailingOrder.IFields.PostageClass.Value,
        shouldUseCredits: boolean,
        paper: Objects.Mailing.IFields.Paper,
        printCoating: Objects.Mailing.IFields.PrintCoating,
        printAndShipQuantity: number | undefined,
        productCode: Objects.Product.Code.Any,
        shippingOption: Objects.MailingOrder.IFields.ShippingOption.Any | undefined,
        isSubsequentOrder: boolean,
        ordersCount: number,
        hStakesQuantity: number | undefined,
        isGrommetHolesIncluded: boolean | undefined,
        discountCode: string | undefined
    ) {
        const params: Params.MailingOrder.GetPriceBreakdown = {
            asvOptions,
            establishmentId,
            isSubsequentOrder,
            mailingListId: undefined,
            mailingListIds,
            paper,
            postageClass,
            printAndShipQuantity,
            printCoating,
            productCode,
            shouldUseCredits,
            shippingOption,
            ordersCount,
            hStakesQuantity,
            isGrommetHolesIncluded,
            discountCode,
        }
        const fn: FunctionNames.MailingOrder.GetPriceBreakdown = 'getMailingOrderPriceBreakdown'
        const response: Responses.MailingOrder.GetPriceBreakdown = await Parse.Cloud.run(fn, params)

        return response
    }

    async loadProductPlans(opts: { productGroup: Objects.SubscriptionProduct.Group.Any } | { productId: string }) {
        const collection: Objects.SubscriptionProductPlan.Collection = 'ProductPlan'

        const productPlanListQuery = new Parse.Query<Objects.SubscriptionProductPlan.Parse>(collection)

        if ('productGroup' in opts) {
            productPlanListQuery.equalTo('productGroup', opts.productGroup)
        } else {
            const product: Objects.SubscriptionProduct.ParsePointer = {
                __type: 'Pointer',
                className: 'Product',
                objectId: opts.productId,
            }
            productPlanListQuery.equalTo('product', product)
        }

        return productPlanListQuery.equalTo('isActive', true).equalTo('isDeleted', false).include('product').find()
    }

    async createSubscriptionForProductPlan(paymentMethodId: string, productPlanId: string) {
        const params: Params.Stripe.Subscription.CreateForProductPlan = {
            paymentMethodId,
            productPlanId,
        }
        const fn: FunctionNames.Stripe.Subscription.CreateForProductPlan = 'createSubscriptionForProductPlan'
        const response: Responses.Stripe.Subscription.CreateForProductPlan = await Parse.Cloud.run(fn, params)

        return response
    }

    async createSplitPaymentSubscriptionForProductPlan(paymentDetails: Objects.StripeWrapper.PaymentDetails[], productPlanId: string) {
        const params: Params.Stripe.Subscription.CreateSplitPaymentForProductPlan = {
            paymentDetails,
            productPlanId,
        }
        const fn: FunctionNames.Stripe.Subscription.CreateSplitPaymentForProductPlan = 'createSplitPaymentForProductPlan'
        const response: Responses.Stripe.Subscription.CreateSplitPaymentForProductPlan = await Parse.Cloud.run(fn, params)

        return response
    }

    async attachMyPaymentMethod(paymentMethodId: string) {
        const params: Params.Stripe.PaymentMethod.AttachToMyUser = {
            paymentMethodId,
        }
        const fn: FunctionNames.Stripe.PaymentMethod.AttachToMyUser = 'attachMyPaymentMethod'
        const response: Responses.Stripe.PaymentMethod.AttachToMyUser = await Parse.Cloud.run(fn, params)

        return response
    }
    async detachMyPaymentMethod(paymentMethodId: string) {
        const params: Params.Stripe.PaymentMethod.DetachFromMyUser = {
            paymentMethodId,
        }
        const fn: FunctionNames.Stripe.PaymentMethod.DetachFromMyUser = 'detachMyPaymentMethod'
        const response: Responses.Stripe.PaymentMethod.DetachFromMyUser = await Parse.Cloud.run(fn, params)
        return response
    }

    async getMyDefaultPaymentMethod() {
        const params: Params.Stripe.PaymentMethod.GetMyDefault = {}
        const fn: FunctionNames.Stripe.PaymentMethod.GetMyDefault = 'getMyDefaultPaymentMethod'
        const response: Responses.Stripe.PaymentMethod.GetMyDefault = await Parse.Cloud.run(fn, params)
        return response
    }

    async setMyDefaultPaymentMethod(paymentMethodId: string) {
        const params: Params.Stripe.PaymentMethod.SetMyDefault = {
            paymentMethodId,
        }
        const fn: FunctionNames.Stripe.PaymentMethod.SetMyDefault = 'setMyDefaultPaymentMethod'
        const response: Responses.Stripe.PaymentMethod.SetMyDefault = await Parse.Cloud.run(fn, params)
        return response
    }

    async getActiveSubscriptionPeriods(establishmentId: string): Promise<ActiveSubscription[]> {
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        const activeSubscriptionPeriods = await this.createParseQuery('SubscriptionPeriod', ['product', 'productPlan'])
            .equalTo('establishment', establishmentPointer)
            .lessThanOrEqualTo('validFrom', new Date())
            .greaterThanOrEqualTo('validTo', new Date())
            .equalTo('isEnabled', true)
            .addDescending('validFrom')
            .find()

        return activeSubscriptionPeriods
    }

    async hasParentWithChildAccountFeature(establishmentId: string) {
        const params: Params.Subscription.HasParentWithChildAccountFeature = {
            establishmentId,
        }
        const fn: FunctionNames.Subscription.HasParentWithChildAccountFeature = 'hasParentWithChildAccountFeature'
        const response: Responses.Subscription.HasParentWithChildAccountFeature = await Parse.Cloud.run(fn, params)
        return response
    }

    async updateEstablishmentBrandDesign(
        establishmentId: string,
        establishmentBrandDesignId: string,
        primaryColor: { r: number; g: number; b: number },
        secondaryColor: { r: number; g: number; b: number }
    ) {
        const params: Params.EstablishmentBrandDesign.Update = {
            establishmentBrandDesignId,
            primaryColor,
            secondaryColor,
            establishmentId,
        }
        const fn: FunctionNames.EstablishmentBrandDesign.Update = 'updateEstablishmentBrandDesign'
        const response: Responses.EstablishmentBrandDesign.Update = await Parse.Cloud.run(fn, params)
        return response
    }

    async createOrUpdateEstablishmentAddress(
        establishmentId: string,
        fullName: string,
        address: string,
        address2: string | undefined,
        city: string,
        state: string,
        zip: string
    ) {
        const params: Params.EstablishmentAddress.CreateOrUpdate = {
            establishmentId,
            address,
            address2,
            city,
            fullName,
            state,
            zip,
        }
        const fn: FunctionNames.EstablishmentAddress.CreateOrUpdate = 'createOrUpdateEstablishmentAddress'
        const response: Responses.EstablishmentAddress.CreateOrUpdate = await Parse.Cloud.run(fn, params)

        return response
    }

    async createOrUpdateEstablishmentReturnAddress(establishmentId: string, fullName: string, address: string, city: string, state: string, zip: string) {
        const params: Params.EstablishmentReturnAddress.CreateOrUpdate = {
            establishmentId,
            address,
            city,
            fullName,
            state,
            zip,
        }
        const fn: FunctionNames.EstablishmentReturnAddress.CreateOrUpdate = 'createOrUpdateEstablishmentReturnAddress'
        const response: Responses.EstablishmentReturnAddress.CreateOrUpdate = await Parse.Cloud.run(fn, params)

        return response
    }

    async createEstablishmentBrandDesign(
        establishmentId: string,
        primaryColor: { r: number; g: number; b: number },
        secondaryColor: { r: number; g: number; b: number }
    ) {
        const params: Params.EstablishmentBrandDesign.Create = {
            primaryColor,
            secondaryColor,
            establishmentId,
        }
        const fn: FunctionNames.EstablishmentBrandDesign.Create = 'createEstablishmentBrandDesign'
        const response: Responses.EstablishmentBrandDesign.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    async loadlatestEstablishmentBrandDesign(establishmentId: string) {
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }
        const collection: Objects.EstablishmentBrandDesign.Collection = 'EstablishmentBrandDesign'
        return new Parse.Query<Objects.EstablishmentBrandDesign.Parse>(collection)
            .equalTo('establishment', establishmentPointer)
            .equalTo('isDeleted', false)
            .descending('updatedAt')
            .first()
    }

    async createEstablishmentRelationship(establishmentId: string, childUserEmail: string) {
        const params: Params.EstablishmentRelationship.Create = {
            childUserEmail,
            establishmentId,
        }
        const fn: FunctionNames.EstablishmentRelationship.Create = 'createEstablishmentRelationship'
        const response: Responses.EstablishmentRelationship.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    async batchUpdateEstablishmentRelationships(establishmentId: string, establishmentRelationshipIds: string[], isActive: boolean) {
        const params: Params.EstablishmentRelationship.BatchUpdate = {
            establishmentRelationshipIds,
            establishmentId,
            isActive,
        }
        const fn: FunctionNames.EstablishmentRelationship.BatchUpdate = 'batchUpdateEstablishmentRelationships'
        const response: Responses.EstablishmentRelationship.BatchUpdate = await Parse.Cloud.run(fn, params)

        return response
    }

    async logAccuzipQuote(mailingId: string) {
        const params: Params.Mailing.LogAccuzipQuote = {
            mailingId,
        }
        const fn: FunctionNames.Mailing.LogAccuzipQuote = 'logAccuzipQuote'
        const response: Responses.Mailing.LogAccuzipQuote = await Parse.Cloud.run(fn, params)

        return response
    }

    async loadEstablishmentRelationshipList(establishmentId: string) {
        const ESTABLISHMENT_RELATIONSHIP_COLLECTION: Objects.EstablishmentRelationship.Collection = 'EstablishmentRelationship'
        const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

        const establishmentParent = new Parse.Object(ESTABLISHMENT_COLLECTION) as Objects.Establishment.Parse
        establishmentParent.id = establishmentId

        const establishmentRelationshipList = await new Parse.Query<Objects.EstablishmentRelationship.Parse>(ESTABLISHMENT_RELATIONSHIP_COLLECTION)
            .equalTo('parent', establishmentParent)
            .equalTo('isDeleted', false)
            .limit(Number.MAX_SAFE_INTEGER)
            .find()

        return establishmentRelationshipList
    }

    getChildAccountQuery(establishmentId: string): Parse.Query<Objects.EstablishmentRelationship.Parse> {
        const ESTABLISHMENT_RELATIONSHIP_COLLECTION: Objects.EstablishmentRelationship.Collection = 'EstablishmentRelationship'
        const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

        const establishmentParent = new Parse.Object(ESTABLISHMENT_COLLECTION) as Objects.Establishment.Parse
        establishmentParent.id = establishmentId

        return new Parse.Query<Objects.EstablishmentRelationship.Parse>(ESTABLISHMENT_RELATIONSHIP_COLLECTION)
            .equalTo('parent', establishmentParent)
            .equalTo('isDeleted', false)
    }

    async getEstablishmentRelationshipParents(establishmentId: string) {
        const params: Params.EstablishmentRelationship.GetParents = {
            establishmentId,
        }
        const fn: FunctionNames.EstablishmentRelationship.GetParents = 'getEstablishmentRelationshipParents'
        const response: Responses.EstablishmentRelationship.GetParents = await Parse.Cloud.run(fn, params)

        return response
    }

    async loadEstablishmentBrandDesignList(establishmentId: string) {
        const ESTABLISHMENT_BRAND_DESIGN_COLLECTION: Objects.EstablishmentBrandDesign.Collection = 'EstablishmentBrandDesign'
        const ESTABLISHMENT_COLLECTION: Objects.Establishment.Collection = 'Establishment'

        const establishment = new Parse.Object(ESTABLISHMENT_COLLECTION) as Objects.Establishment.Parse
        establishment.id = establishmentId

        const establishmentBrandDesignList = await new Parse.Query<Objects.EstablishmentBrandDesign.Parse>(ESTABLISHMENT_BRAND_DESIGN_COLLECTION)
            .equalTo('establishment', establishment)
            .equalTo('isDeleted', false)
            .find()

        return establishmentBrandDesignList
    }

    async loadEstablishmentAddress(establishmentId: string) {
        const ESTABLISHMENT_ADDRESS_COLLECTION: Objects.EstablishmentAddress.Collection = 'EstablishmentAddress'
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        const establishmentRelationshipList = await new Parse.Query<Objects.EstablishmentAddress.Parse>(ESTABLISHMENT_ADDRESS_COLLECTION)
            .equalTo('establishment', establishmentPointer)
            .equalTo('isDeleted', false)
            .ascending('createdAt')
            .first()

        return establishmentRelationshipList
    }

    async loadEstablishmentReturnAddress(establishmentId: string) {
        const ESTABLISHMENT_ADDRESS_COLLECTION: Objects.EstablishmentReturnAddress.Collection = 'EstablishmentReturnAddress'
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        const establishmentReturnAddress = await new Parse.Query<Objects.EstablishmentReturnAddress.Parse>(ESTABLISHMENT_ADDRESS_COLLECTION)
            .equalTo('establishment', establishmentPointer)
            .equalTo('isDeleted', false)
            .ascending('createdAt')
            .first()

        return establishmentReturnAddress
    }

    async loadPreviouslyCreatedEstablishmentBrands(limit: number) {
        const ESTABLISHMENT_BRAND_COLLECTION: Objects.EstablishmentBrand.Collection = 'EstablishmentBrand'

        const establishmentBrand = await new Parse.Query<Objects.EstablishmentBrand.Parse>(ESTABLISHMENT_BRAND_COLLECTION)
            .descending('createdAt')
            .include('establishment')
            .limit(limit)
            .find()

        return establishmentBrand
    }

    async loadEstablishmentBrandAsset(objectId: string) {
        const ESTABLISHMENT_BRANDASSET_COLLECTION: Objects.EstablishmentBrandAsset.Collection = 'EstablishmentBrandAsset'
        const establishmentBrandAsset = await new Parse.Query<Objects.EstablishmentBrandAsset.Parse>(ESTABLISHMENT_BRANDASSET_COLLECTION)
            .equalTo('objectId', objectId)
            .first()

        return establishmentBrandAsset
    }

    async loadEstablishmentAddressMailingList(establishmentId: string) {
        const MAILING_LIST_COLLECTION: Objects.MailingList.Collection = 'MailingList'
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        const allListTypes: Objects.MailingList.ListType.All = ['ASV-SCAN-LIST', 'ASV-TOOL-LEAD-LIST', 'MY-ADDRESS-LIST', 'STANDARD']
        const myAddressListTypes = allListTypes.filter((listType) => listType === 'MY-ADDRESS-LIST')

        const mailingList = await new Parse.Query<Objects.MailingList.Parse>(MAILING_LIST_COLLECTION)
            .equalTo('establishment', establishmentPointer)
            .containedIn('listType', myAddressListTypes)
            .equalTo('isDeleted', false)
            .descending('createdAt')
            .first()

        return mailingList
    }

    async createMailingOrderBadAddressAsAdmin(establishmentId: string, mailingOrderId: string) {
        const params: Params.MailingOrderBadAddress.CreateAsAdmin = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrderBadAddress.CreateAsAdmin = 'createMailingOrderBadAddressAsAdmin'
        const response: Responses.MailingOrderBadAddress.CreateAsAdmin = await Parse.Cloud.run(fn, params)

        return response
    }

    async createMailingOrderBadAddress(establishmentId: string, mailingOrderId: string) {
        const params: Params.MailingOrderBadAddress.Create = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrderBadAddress.Create = 'createMailingOrderBadAddress'
        const response: Responses.MailingOrderBadAddress.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    async getMailingOrderBadAddress(establishmentId: string, mailingOrderId: string) {
        const params: Params.MailingOrderBadAddress.Get = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrderBadAddress.Get = 'getMailingOrderBadAddress'
        const response: Responses.MailingOrderBadAddress.Get = await Parse.Cloud.run(fn, params)

        return response
    }

    async getMailingOrderBadAddressAsAdmin(establishmentId: string, mailingOrderId: string) {
        const params: Params.MailingOrderBadAddress.GetAsAdmin = {
            establishmentId,
            mailingOrderId,
        }
        const fn: FunctionNames.MailingOrderBadAddress.GetAsAdmin = 'getMailingOrderBadAddressAsAdmin'
        const response: Responses.MailingOrderBadAddress.GetAsAdmin = await Parse.Cloud.run(fn, params)

        return response
    }

    async cancelMailingOrders(mailingOrderIds: string[]) {
        const params: Params.MailingOrder.Cancel = {
            mailingOrderIds,
        }
        const fn: FunctionNames.MailingOrder.Cancel = 'cancelMailingOrders'
        const response: Responses.MailingOrder.Cancel = await Parse.Cloud.run(fn, params)

        return response
    }

    async getDesignEditorURL(establishmentId: string, mailingOrderId: string, editorType: Objects.ChiliConfig.EditorType) {
        const hasWisePelicanOpsMemberRole = await this.roleService.doesUserHavePermission('WP_Ops Member')

        const fromSubDomain = window.location.hostname.endsWith(WP_DOMAIN)

        if (hasWisePelicanOpsMemberRole) {
            const params: Params.MailingOrder.GetDesignEditorURLAsAdmin = {
                establishmentId,
                mailingOrderId,
                editorType,
                fromSubDomain,
            }
            const fn: FunctionNames.MailingOrder.GetDesignEditorURLAsAdmin = 'getMailingOrderDesignEditorURLAsAdmin'
            const response: Responses.MailingOrder.GetDesignEditorURLAsAdmin = await Parse.Cloud.run(fn, params)

            return response
        } else {
            const params: Params.MailingOrder.GetDesignEditorURLAsClient = {
                establishmentId,
                mailingOrderId,
                editorType,
                fromSubDomain,
            }
            const fn: FunctionNames.MailingOrder.GetDesignEditorURLAsClient = 'getMailingOrderDesignEditorURLAsClient'
            const response: Responses.MailingOrder.GetDesignEditorURLAsClient = await Parse.Cloud.run(fn, params)

            return response
        }
    }

    async getMailingOrderDesignPreviewURL(establishmentId: string, mailingOrderId: string, page: 'front' | 'back' | 'previewPage3' | 'previewPage4') {
        const params: Params.MailingOrder.GetDesignPreviewURL = {
            establishmentId,
            mailingOrderId,
            page,
        }
        const fn: FunctionNames.MailingOrder.GetDesignPreviewURL = 'getMailingOrderDesignPreviewURL'
        const response: Responses.MailingOrder.GetDesignPreviewURL = await Parse.Cloud.run(fn, params)

        return response
    }

    async createMailingOrderDraft(establishmentId: string, designTemplate: Objects.Job.ByType.MailingOrder.CreateDraft.Params.Fields.DesignTemplate.Value.Any) {
        const params: Params.MailingOrder.CreateDraft = {
            establishmentId,
            designTemplate,
        }
        const fn: FunctionNames.MailingOrder.CreateDraft = 'createMailingOrderDraft'
        const response: Responses.MailingOrder.CreateDraft = await Parse.Cloud.run(fn, params)

        return response
    }

    /**
     * This will create a reference/url for the new image.
     * The image should be uploaded separately using the upload service
     */
    async createImage(establishmentId: string, fileName: string, category: Objects.Image.Category | undefined, subcategory: string | undefined) {
        const params: Params.Image.Create = {
            establishmentId,
            fileName,
            category,
            subcategory,
        }
        const fn: FunctionNames.Image.Create = 'createImage'
        const response: Responses.Image.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    async processImage(establishmentId: string, imageID: string) {
        const params: Params.Image.Process = {
            establishmentId,
            imageID,
        }
        const fn: FunctionNames.Image.Process = 'processImage'
        const response: Responses.Image.Process = await Parse.Cloud.run(fn, params)

        return response
    }

    async markImageAsDeleted(imageId: string, establishmentId: string) {
        const params: Params.Image.MarkImageAsDeleted = {
            imageId,
            establishmentId,
        }
        const fn: FunctionNames.Image.MarkImageAsDeleted = 'markImageAsDeleted'
        const response: Responses.Image.MarkImageAsDeleted = await Parse.Cloud.run(fn, params)
        return response
    }

    async getImage(imageID: string) {
        const IMAGE_COLLECTION: Objects.Image.Collection = 'Image'

        const image = await new Parse.Query<Objects.Image.Parse>(IMAGE_COLLECTION).equalTo('objectId', imageID).equalTo('isDeleted', false).first()

        return image
    }

    async loadImages(
        establishmentID: string,
        category: Objects.Image.Category | undefined,
        subcategory: string | undefined,
        includeDeleted: boolean,
        skip: number,
        limit: number
    ) {
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentID,
        }
        const IMAGE_COLLECTION: Objects.Image.Collection = 'Image'
        const query = new Parse.Query<Objects.Image.Parse>(IMAGE_COLLECTION).equalTo('establishment', establishmentPointer).equalTo('status', 'PROCESSED')

        if (!includeDeleted) {
            query.equalTo('isDeleted', false)
        }

        if (category) {
            query.equalTo('category', category)
        }

        if (subcategory) {
            query.equalTo('subcategory', subcategory)
        }

        query.skip(skip).limit(limit)

        const images = await query.descending('createdAt').find()

        return images
    }

    async updateImage(establishmentId: string, imageId: string, deleted: boolean | undefined): Promise<boolean> {
        const params: Params.Image.Update = {
            establishmentId,
            imageId,
            deleted,
        }

        const fn: FunctionNames.Image.Update = 'updateImage'
        return this.callCloudWithLoginHandler<Responses.Image.Update>(fn, params)
    }

    async getRekognitionFacialDetection(objectId: string): Promise<Objects.RekognitionFacialDetection.Parse | undefined> {
        const REKOGNITION_COLLETCION: Objects.RekognitionFacialDetection.Collection = 'RekognitionFacialDetection'
        const query = await new Parse.Query<Objects.RekognitionFacialDetection.Parse>(REKOGNITION_COLLETCION).equalTo('objectId', objectId).first()

        return query
    }

    private findUsersByNameQuery(partialFullName: string) {
        const firstNameKey: keyof Objects.User.Attributes = 'firstName'
        const lastNameKey: keyof Objects.User.Attributes = 'lastName'

        return Parse.Query.or(
            ...[
                new Parse.Query<Objects.User._Parse>(Parse.User)

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    .equalTo(firstNameKey, { $regex: partialFullName, $options: 'i' }),

                new Parse.Query<Objects.User._Parse>(Parse.User)

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    .equalTo(lastNameKey, { $regex: partialFullName, $options: 'i' }),
            ]
        )
    }

    async findUsersByName(partialFullName: string) {
        const [users, count] = await Promise.all([
            this.findUsersByNameQuery(partialFullName).ascending(['firstName', 'lastName']).limit(50).find(),

            this.findUsersByNameQuery(partialFullName).count(),
        ])

        return {
            users,
            count,
        }
    }
    async getUsersFullNames(userIds: string[]) {
        const users = await new Parse.Query<Objects.User._Parse>(Parse.User).containedIn('objectId', userIds).limit(userIds.length).find()

        const fullNames: Record<string, string | undefined> = {}

        for (const user of users) {
            fullNames[user.id] = `${user.get('firstName')} ${user.get('lastName')}`
        }

        return fullNames
    }

    // async getBrokerageBrands() {
    //     const COLLECTION: Objects.BrokerageBrand.Collection = 'BrokerageBrand'
    //     const query = new Parse.Query<Objects.BrokerageBrand.Parse>(COLLECTION)

    //     const brokerageBrands = await query.ascending('name')
    //         .find()

    //     return brokerageBrands
    // }

    async getOnboardingForm(establishmentId: string) {
        const COLLECTION: Objects.OnboardingForm.Collection = 'OnboardingForm'

        const establishmentPtr: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        return new Parse.Query<Objects.OnboardingForm.Parse>(COLLECTION)
            .equalTo('establishment', establishmentPtr)
            .include('companyLogo', 'headshot')
            .descending('createdAt')
            .first()
    }

    async getOnboardingStatus(establishmentId: string) {
        const COLLECTION: Objects.OnboardingStatus.Collection = 'OnboardingStatus'

        const establishmentPtr: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }

        return new Parse.Query<Objects.OnboardingStatus.Parse>(COLLECTION).equalTo('establishment', establishmentPtr).descending('createdAt').first()
    }

    async applyBrokerageBrandToMasterTemplate(automatedDesignId: string, brokerageBrandId: string) {
        console.log(automatedDesignId, brokerageBrandId)
    }

    async loadMailingsForMailingOrder(mailingOrderID: string) {
        const COLLECTION: Objects.Mailing.Collection = 'Mailing'
        const mailingOrderPointer: Objects.MailingOrder.ParsePointer = {
            __type: 'Pointer',
            className: 'MailingOrder',
            objectId: mailingOrderID,
        }

        const mailings = await new Parse.Query<Objects.Mailing._Parse.ForMailingOrderQuery>(COLLECTION).equalTo('mailingOrders', mailingOrderPointer).find()

        return mailings as unknown as Objects.Mailing._Parse[]
    }

    async loadMailingOrdersForMailing(mailingID: string) {
        const COLLECTION: Objects.Mailing.Collection = 'Mailing'
        const mailing = new Parse.Object(COLLECTION) as Objects.Mailing._Parse
        mailing.id = mailingID

        const mailingOrders = await mailing.relation<Objects.MailingOrder.Parse, 'mailingOrders'>('mailingOrders').query().limit(Number.MAX_SAFE_INTEGER).find()

        return mailingOrders
    }

    async revertMailingOrders(mailingId: string) {
        const params: Params.Mailing.RevertMailingOrders = {
            mailingId,
        }
        const fn: FunctionNames.Mailing.RevertMailingOrders = 'revertMailingOrders'
        const response: Responses.Mailing.RevertMailingOrders = await Parse.Cloud.run(fn, params)

        return response
    }

    async cancelMailing(mailingId: string) {
        const params: Params.Mailing.Cancel = {
            mailingId,
        }
        const fn: FunctionNames.Mailing.Cancel = 'cancelMailing'
        const response: Responses.Mailing.Cancel = await Parse.Cloud.run(fn, params)

        return response
    }

    async updateMailingOrderMissingCarrierRouteSummaries() {
        const params: Params.MailingOrder.UpdateMissingCarrierRouteSummaries = {} // unused
        const fn: FunctionNames.MailingOrder.UpdateMissingCarrierRouteSummaries = 'updateMailingOrderMissingCarrierRouteSummaries'
        const response: Responses.MailingOrder.UpdateMissingCarrierRouteSummaries = await Parse.Cloud.run(fn, params)
        return response
    }

    async notifyMailingOrderSubmissionAsAdmin(establishmentId: string, mailingOrderId: string, mailingOrderName: string) {
        const params: Params.MailingOrder.NotifySubmissionAsAdmin = {
            establishmentId,
            mailingOrderId,
            mailingOrderName,
        }
        const fn: FunctionNames.MailingOrder.NotifySubmissionAsAdmin = 'notifyMailingOrderSubmissionAsAdmin'
        const response: Responses.MailingOrder.NotifySubmissionAsAdmin = await Parse.Cloud.run(fn, params)
        return response
    }

    async notifyMailingOrderSubmissionAsClient(establishmentId: string, mailingOrderId: string, mailingOrderName: string) {
        const params: Params.MailingOrder.NotifySubmissionAsClient = {
            establishmentId,
            mailingOrderId,
            mailingOrderName,
        }
        const fn: FunctionNames.MailingOrder.NotifySubmissionAsClient = 'notifyMailingOrderSubmissionAsClient'
        const response: Responses.MailingOrder.NotifySubmissionAsClient = await Parse.Cloud.run(fn, params)
        return response
    }

    async sendFacebookPixelAddToCartEvent(eventID: string, eventSourceURL: string, fbc: string | undefined, fbp: string | undefined, value: number) {
        const params: Params.Event.FacebookPixel.AddToCart = {
            eventID,
            eventSourceURL,
            fbc,
            fbp,
            value,
        }
        const fn: FunctionNames.Event.FacebookPixel.AddToCart = 'sendFacebookPixelAddToCartEvent'
        const response: Responses.Event.FacebookPixel.AddToCart = await Parse.Cloud.run(fn, params)
        return response
    }

    async sendFacebookPixelCompleteRegistrationEvent(eventID: string, eventSourceURL: string, fbc: string | undefined, fbp: string | undefined) {
        const params: Params.Event.FacebookPixel.CompleteRegistration = {
            eventID,
            eventSourceURL,
            fbc,
            fbp,
        }
        const fn: FunctionNames.Event.FacebookPixel.CompleteRegistration = 'sendFacebookPixelCompleteRegistrationEvent'
        const response: Responses.Event.FacebookPixel.CompleteRegistration = await Parse.Cloud.run(fn, params)
        return response
    }

    async sendFacebookPixelInitiateCheckoutEvent(eventID: string, eventSourceURL: string, fbc: string | undefined, fbp: string | undefined, value: number) {
        const params: Params.Event.FacebookPixel.InitiateCheckout = {
            eventID,
            eventSourceURL,
            fbc,
            fbp,
            value,
        }
        const fn: FunctionNames.Event.FacebookPixel.InitiateCheckout = 'sendFacebookPixelInitiateCheckoutEvent'
        const response: Responses.Event.FacebookPixel.InitiateCheckout = await Parse.Cloud.run(fn, params)
        return response
    }

    async sendFacebookPixelPageViewEvent(eventID: string, eventSourceURL: string, fbc: string | undefined, fbp: string | undefined) {
        const params: Params.Event.FacebookPixel.PageView = {
            eventID,
            eventSourceURL,
            fbc,
            fbp,
        }
        const fn: FunctionNames.Event.FacebookPixel.PageView = 'sendFacebookPixelPageViewEvent'
        const response: Responses.Event.FacebookPixel.PageView = await Parse.Cloud.run(fn, params)
        return response
    }

    async sendFacebookPixelPurchaseEvent(eventID: string, eventSourceURL: string, fbc: string | undefined, fbp: string | undefined, value: number) {
        const params: Params.Event.FacebookPixel.Purchase = {
            eventID,
            eventSourceURL,
            fbc,
            fbp,
            value,
        }
        const fn: FunctionNames.Event.FacebookPixel.Purchase = 'sendFacebookPixelPurchaseEvent'
        const response: Responses.Event.FacebookPixel.Purchase = await Parse.Cloud.run(fn, params)
        return response
    }

    async sendFacebookPixelViewContentEvent(eventID: string, eventSourceURL: string, fbc: string | undefined, fbp: string | undefined) {
        const params: Params.Event.FacebookPixel.ViewContent = {
            eventID,
            eventSourceURL,
            fbc,
            fbp,
        }
        const fn: FunctionNames.Event.FacebookPixel.ViewContent = 'sendFacebookPixelViewContentEvent'
        const response: Responses.Event.FacebookPixel.ViewContent = await Parse.Cloud.run(fn, params)
        return response
    }

    getDeferredPaymentsQuery() {
        const COLLECTION: Objects.DeferredPayment.Collection = 'DeferredPayment'

        return new Parse.Query<Objects.DeferredPayment.Parse_.Any>(COLLECTION).include(['mailingOrder', 'payment', 'establishment', 'user'])
    }

    async createPrintAndShipMailing(mailingName: string, mailingDate: Date, mailingOrders: { id: string; name: string }[]) {
        const params: Params.Mailing.CreatePrintAndShip = {
            mailingDate,
            mailingName,
            mailingOrders,
        }

        const fn: FunctionNames.Mailing.CreatePrintAndShip = 'createPrintAndShipMailing'
        const response = await this.callCloudWithLoginHandler<Responses.Mailing.CreatePrintAndShip>(fn, params)

        return response
    }

    async createXpressdocsMailing(mailingName: string, mailingDate: Date, mailingOrders: { id: string; name: string }[]) {
        const params: Params.Mailing.CreateXpressdocs = {
            mailingDate,
            mailingName,
            mailingOrders,
        }

        const fn: FunctionNames.Mailing.CreateXpressdocs = 'createXpressdocsMailing'
        const response = await this.callCloudWithLoginHandler<Responses.Mailing.CreateXpressdocs>(fn, params)

        return response
    }

    getDesignTemplateSiblingsQuery() {
        const COLLECTION: Objects.DesignTemplateSibling.Collection = 'DesignTemplateSibling'

        return new Parse.Query<Objects.DesignTemplateSibling.Parse_>(COLLECTION).include(['designTemplateA', 'designTemplateB'])
    }

    getDesignTemplatesQuery() {
        const COLLECTION: Objects.DesignTemplate.Collection = 'DesignTemplate'

        return new Parse.Query<Objects.DesignTemplate.Parse_.Any>(COLLECTION)
    }

    getTemplateSiblingsByTemplateIdQuery(templateId: string) {
        const DESIGN_TEMPLATE_COLLECTION: Objects.DesignTemplate.Collection = 'DesignTemplate'
        const designTemplateParseObject = new Parse.Object(DESIGN_TEMPLATE_COLLECTION) as Objects.DesignTemplate.Parse_.Any
        designTemplateParseObject.id = templateId

        const designTemplateSiblingAQuery = this.getDesignTemplateSiblingsQuery().equalTo('designTemplateA', designTemplateParseObject)

        const designTemplateSiblingBQuery = this.getDesignTemplateSiblingsQuery().equalTo('designTemplateB', designTemplateParseObject)

        return Parse.Query.or<Objects.DesignTemplateSibling.Parse_>(designTemplateSiblingAQuery, designTemplateSiblingBQuery).include(
            'designTemplateA',
            'designTemplateB'
        )
    }

    async createOrUpdateDesignTemplateSibling(designTemplateAId: string, designTemplateBId: string, isEnabled: boolean) {
        const params: Params.DesignTemplateSibling.CreateOrUpdate = {
            designTemplateAId,
            designTemplateBId,
            isEnabled,
        }

        const fn: FunctionNames.DesignTemplateSibling.CreateOrUpdate = 'createOrUpdateDesignTemplateSibling'
        const response: Responses.DesignTemplateSibling.CreateorUpdate = await Parse.Cloud.run(fn, params)

        return response
    }

    async removeDesignTemplateSibling(designTemplateSiblingId: string) {
        const params: Params.DesignTemplateSibling.Remove = {
            designTemplateSiblingId,
        }

        const fn: FunctionNames.DesignTemplateSibling.Remove = 'removeDesignTemplateSibling'
        const response: Responses.DesignTemplateSibling.Remove = await Parse.Cloud.run(fn, params)

        return response
    }

    getTotalSubscriptionCredit(subscriptionCredits: Objects.SubscriptionCredit.Parse[]) {
        return (
            subscriptionCredits?.reduce((totalCredit, currentSubscription) => {
                totalCredit += currentSubscription.get('amount') || 0
                return totalCredit
            }, 0) ?? 0
        )
    }

    async getSubscriptionPlans() {
        const COLLECTION: Objects.SubscriptionPlan.Collection = 'SubscriptionPlan'

        return new Parse.Query<Objects.SubscriptionPlan.Parse_.Any>(COLLECTION).find()
    }

    async getProduct(productId: string) {
        const COLLECTION: Objects.SubscriptionProduct.Collection = 'Product'

        return new Parse.Query<Objects.SubscriptionProduct.Fetched>(COLLECTION).equalTo('objectId', productId).first()
    }

    async getProductPlansFromProduct(productId: string) {
        const COLLECTION: Objects.SubscriptionProductPlan.Collection = 'ProductPlan'

        const productPointer: Objects.SubscriptionProduct.ParsePointer = {
            __type: 'Pointer',
            className: 'Product',
            objectId: productId,
        }

        return new Parse.Query<Objects.SubscriptionProductPlan.Parse>(COLLECTION)
            .equalTo('product', productPointer)
            .equalTo('isActive', true)
            .equalTo('isDeleted', false)
            .limit(Number.MAX_SAFE_INTEGER)
            .find()
    }

    async createCreditSubscriptionFromProduct(
        paymentMethodId: string,
        productId: string,
        monthlyCardAmount: number,
        expectedPostcardPrice: number,
        interval: 'month' | 'quarter' | 'year', // TODO Semi Annual Credits
        withASVSurcharge: boolean,
        checkUvCoatingEligibility: boolean
    ) {
        const params: Params.Stripe.Subscription.CreateCreditSubscriptionFromProduct = {
            paymentMethodId,
            productId,
            expectedPostcardPrice,
            monthlyCardAmount,
            withASVSurcharge,
            interval,
            checkUvCoatingEligibility,
        }
        const fn: FunctionNames.Stripe.Subscription.CreateCreditSubscriptionFromProduct = 'createCreditSubscriptionFromProduct'
        const response: Responses.Stripe.Subscription.CreateCreditSubscriptionFromProduct = await Parse.Cloud.run(fn, params)

        return response
    }

    async createSplitPaymentCreditSubscriptionForProductPlan(
        paymentDetails: Objects.StripeWrapper.PaymentDetails[],
        productId: string,
        monthlyCardAmount: number,
        expectedPostcardPrice: number,
        interval: 'month' | 'quarter' | 'year', // TODO Semi Annual Credits
        withASVSurcharge: boolean,
        checkUvCoatingEligibility: boolean
    ) {
        const params: Params.Stripe.Subscription.CreateSplitPaymentCreditSubscriptionFromProduct = {
            paymentDetails,
            productId,
            monthlyCardAmount,
            expectedPostcardPrice,
            interval,
            withASVSurcharge,
            checkUvCoatingEligibility,
        }
        const fn: FunctionNames.Stripe.Subscription.CreateSplitPaymentCreditSubscriptionFromProduct = 'createSplitPaymentCreditSubscriptionFromProduct'
        const response: Responses.Stripe.Subscription.CreateSplitPaymentCreditSubscriptionFromProduct = await Parse.Cloud.run(fn, params)
        return response
    }

    async getMyCreditHistory() {
        const params: Params.CreditHistory.GetMy = {}

        const fn: FunctionNames.CreditHistory.GetMy = 'getMyCreditHistory'
        const response: Responses.CreditHistory.GetMy = await Parse.Cloud.run(fn, params)

        return response
    }

    async getCreditHistoryAsWPOps(establishmentId: string) {
        const params: Params.CreditHistory.GetAsWPOps = {
            establishmentId,
        }

        const fn: FunctionNames.CreditHistory.GetAsWPOps = 'getCreditHistoryAsWPOps'
        const response: Responses.CreditHistory.GetAsWPOps = await Parse.Cloud.run(fn, params)

        return response
    }

    async getTreatmentForImage(imageId: string, additionalParameters: Objects.ImageTreatment.AdditionalParameters) {
        const imagePointer: Objects.Image.ParsePointer = {
            __type: 'Pointer',
            className: 'Image',
            objectId: imageId,
        }

        const targetAspectRatio = additionalParameters.frameAspectRatio
        if (!targetAspectRatio) {
            return undefined
        }

        const COLLECTION: Objects.ImageTreatment.Collection = 'ImageTreatment'
        const imageTreatment = await new Parse.Query<Objects.ImageTreatment.Parse>(COLLECTION)
            .equalTo('image', imagePointer)
            .equalTo('frameAspectRatio', +targetAspectRatio.toFixed(2)) // Round target aspect ratio to 2 decimal places
            .descending('updatedAt')
            .first()

        if (!imageTreatment) {
            return undefined
        }

        return {
            imageCropHeight: imageTreatment.get('imageCropHeight'),
            imageCropWidth: imageTreatment.get('imageCropWidth'),
            imageCropX: imageTreatment.get('imageCropX'),
            imageCropY: imageTreatment.get('imageCropY'),
            frameAspectRatio: imageTreatment.get('frameAspectRatio'),
        }
    }

    async createImageTreatment(imageId: string, establishmentId: string, imageTreatment: Objects.ImageTreatment.TreatmentData) {
        const params: Params.ImageTreatment.Create = {
            imageId,
            establishmentId,
            imageTreatment,
        }

        const fn: FunctionNames.ImageTreatment.Create = 'createImageTreatment'
        const response: Responses.ImageTreatment.Create = await Parse.Cloud.run(fn, params)

        return response
    }

    loadExpressEditorAssetsByGroup(establishmentID: string, group: string) {
        const COLLECTION: Objects.SimpleEditorAsset.Collection = 'SimpleEditorAsset'
        const establishment: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentID,
        }
        return new Parse.Query<Objects.SimpleEditorAsset.Parse>(COLLECTION)
            .equalTo('establishment', establishment)
            .equalTo('group', group)
            .notEqualTo('isDeleted', true)
            .limit(Number.MAX_SAFE_INTEGER)
            .find()
    }

    loadSubscriptionAddOnPlan() {
        const COLLECTION: Objects.SubscriptionAddOnPlan.Collection = 'SubscriptionAddOnPlan'
        return new Parse.Query<Objects.SubscriptionAddOnPlan.Parse>(COLLECTION).equalTo('version', 1).equalTo('isEnabled', true).first()
    }

    getReferrer(userId: string) {
        const userPointer: Objects.User.ParsePointer = {
            __type: 'Pointer',
            className: '_User',
            objectId: userId,
        }
        const COLLECTION: Objects.Referral.Collection = 'Referral'
        return new Parse.Query<Objects.Referral.Parse>(COLLECTION).equalTo('user', userPointer).first()
    }

    async searchForMlsAgent(establishmentId: string, searchValue: string) {
        const params: Params.Mls.AgentSearch = {
            establishmentId,
            searchValue,
        }

        const fn: FunctionNames.Mls.AgentSearch = 'searchMlsAgent'
        const response: Responses.Mls.AgentSearch = await Parse.Cloud.run(fn, params)

        return response
    }

    async findMlsListings(establishmentId: string, agentID: string) {
        const params: Params.Mls.FindListings = {
            establishmentId,
            agentID,
        }

        const fn: FunctionNames.Mls.FindListings = 'findMlsListings'
        const response: Responses.Mls.FindListings = await Parse.Cloud.run(fn, params)

        return response
    }

    getMlsPostSignUpMatches(establishmentId: string) {
        const establishmentPointer: Objects.Establishment.ParsePointer = {
            __type: 'Pointer',
            className: 'Establishment',
            objectId: establishmentId,
        }
        const COLLECTION: Objects.MlsPostSignUpProfileMatch.Collection = 'MlsPostSignUpProfileMatch'
        return new Parse.Query<Objects.MlsPostSignUpProfileMatch.Parse>(COLLECTION).equalTo('establishment', establishmentPointer).first()
    }

    async submitMlsPostSignUpProfileSelection(establishmentId: string, selectedProfile: Objects.Establishment.Field.MlsProfile) {
        const params: Params.MlsPostSignUpProfileMatch.SubmitSelection = {
            establishmentId,
            selectedProfile,
        }

        const fn: FunctionNames.MlsPostSignUpProfileMatch.SubmitSelection = 'submitMlsPostSignUpProfileSelection'
        const response: Responses.MlsPostSignUpProfileMatch.SubmitSelection = await Parse.Cloud.run(fn, params)

        return response
    }

    async updateEstablishmentSettings(establishmentId: string, settings: Partial<Objects.Establishment.Field.Settings>) {
        const params: Params.Establishment.UpdateSettings = {
            establishmentId,
            settings,
        }

        const fn: FunctionNames.Establishment.UpdateSettings = 'updateEstablishmentSettings'
        const response: Responses.Establishment.UpdateSettings = await Parse.Cloud.run(fn, params)

        return response
    }

    async isLocalDeliveryEnabled(zipCode: string) {
        const params: Params.DeliveryAddress.IsLocalDeliveryEnabled = {
            zipCode,
        }

        const fn: FunctionNames.DeliveryAddress.IsLocalDeliveryEnabled = 'isLocalDeliveryEnabled'
        const response: Responses.DeliveryAddress.IsLocalDeliveryEnabled = await Parse.Cloud.run(fn, params)

        return response
    }

    // Should be same as the one in the server ux-config-establishment.ts
    private getUXConfigSummaryQuery(establishmentID: string) {
        const UX_CONFIG_ESTABLISHMENT_COLLECTION: Objects.UXConfigEstablishment.Collection = 'UXConfigEstablishment'
        const query = new Parse.Query<Objects.UXConfigEstablishment.Parse>(UX_CONFIG_ESTABLISHMENT_COLLECTION)
            .equalTo('establishment', { __type: 'Pointer', objectId: establishmentID, className: 'Establishment' })
            .equalTo('isEnabled', true)
            .descending('sort')
            .include('uxConfig')
            .limit(Number.MAX_SAFE_INTEGER)

        return query
    }

    public async generateVisitorIdentificationToken() {
        const fn: FunctionNames.Hubspot.GenerateVisitorIdentificationToken = 'generateVisitorIdentificationToken'
        const params: Params.Hubspot.GenerateVisitorIdentificationToken = {}
        const response: Responses.Hubspot.GenerateVisitorIdentificationToken = await Parse.Cloud.run(fn, params)
        return response
    }

    public async getDesignTemplatesGroupedByCollectionName(
        productType: Objects.Product.Type.Any,
        productTypeVariant: Objects.Product.TypeVariant.Any | undefined,
        uxConfigTags: string[] | undefined,
        limit = 10,
        skip = 0
    ) {
        const params: Params.DesignTemplate.GetDesignTemplatesGroupedByCollectionName = {
            productType,
            productTypeVariant,
            uxConfigTags,
            limit,
            skip,
        }

        const fn: FunctionNames.DesignTemplate.GetDesignTemplatesGroupedByCollectionName = 'getDesignTemplatesGroupedByCollectionName'
        const response: Responses.DesignTemplate.GetDesignTemplatesGroupedByCollectionName = await Parse.Cloud.run(fn, params)
        return response
    }
    callParseEndpoint(
        fn: FunctionNames.MailingListOrder.GetCoordinateForAddress,
        params: Params.MailingListOrder.GetCoordinateForAddress
    ): Promise<Responses.MailingListOrder.GetCoordinateForAddress>
    callParseEndpoint(fn: string, params: object) {
        return Parse.Cloud.run(fn, params)
    }

    async getMyIpAddress(): Promise<{ ip: string }> {
        const fn: FunctionNames.User.SignUp.GetMyIpAddress = 'getMyIpAddress'
        const response = await Parse.Cloud.run(fn)

        return response
    }

    async getAutogenPreviewRequestForTemplatePreview(
        establishmentID: string,
        productCode: Objects.Product.Code.Any
    ): Promise<Objects.AutogenPreviewRequest.Fetched | undefined> {
        const AUTOMATED_PREVIEW_REQUEST_COLLECTION: Objects.AutogenPreviewRequest.Collection = 'AutogenPreviewRequest'
        const autogenPreviewRequest = await new Parse.Query<Objects.AutogenPreviewRequest.Fetched>(AUTOMATED_PREVIEW_REQUEST_COLLECTION)
            .equalTo('establishment', this.createParseObject('Establishment', establishmentID))
            .equalTo('productCode', productCode)
            .greaterThan('validTo', new Date())
            .descending('createdAt')
            .first()

        return autogenPreviewRequest
    }

    async createMailingOrderDraftFromAutogenPreviewRequest(autogenPreviewRequestId: string, establishmentId: string) {
        const params: Params.MailingOrder.CreateDraftFromAutogenPreviewRequest = {
            autogenPreviewRequestId,
            establishmentId,
        }
        const fn: FunctionNames.MailingOrder.CreateDraftFromAutogenPreviewRequest = 'createMailingOrderDraftFromAutogenPreviewRequest'
        const response: Responses.MailingOrder.CreateDraftFromAutogenPreviewRequest = await Parse.Cloud.run(fn, params)

        return response
    }

    // Should be similar to as src\types\utils.ts
    createParseObject<T extends string | undefined = undefined>(
        className: Objects.Establishment.Collection,
        id?: T
    ): Parse.Object.UnFetched<Objects.Establishment.Attributes, T>
    createParseObject<T extends string | undefined = undefined>(
        className: Objects.FacebookPixelEvent.Collection,
        id?: T
    ): Parse.Object.UnFetched<Objects.FacebookPixelEvent.Attributes, T>
    createParseObject<T extends string | undefined = undefined>(className: string, id?: T) {
        const p = new Parse.Object(className)
        if (id) {
            p.id = id
        }
        return p
    }

    // Should be similar to as src\types\utils.ts
    createParseQuery<T extends Objects.MailingList.Fetched, U extends (keyof T['attributes'])[]>(
        className: 'MailingList',
        include?: U
    ): Parse.Query.WithoutInclude<T, Parse.Object.IncludePointerAttributes<T, U>>
    createParseQuery<T extends Objects.SubscriptionPeriod.Fetched, U extends (keyof T['attributes'])[]>(
        className: 'SubscriptionPeriod',
        include?: U
    ): Parse.Query.WithoutInclude<T, Parse.Object.IncludePointerAttributes<T, U>>
    createParseQuery<T extends Parse.Attributes>(className: string, include?: (keyof T)[]) {
        const query = new Parse.Query(className)
        if (include) {
            // @ts-expect-error this is fine
            query.include(...include)
        }
        return query
    }

    async getFiltersByProductCode(productCode: Objects.Product.Code.Any): Promise<Objects.TemplateSelectionFilterGrouping.AnyFilter[]> {
        try {
            const TEMPLATE_SELECTION_FILTER_GROUPING_COLLECTION: Objects.TemplateSelectionFilterGrouping.Collection = 'TemplateSelectionFilterGrouping'
            const templateFiltersQuery = new Parse.Query<Objects.TemplateSelectionFilterGrouping.Parse>(TEMPLATE_SELECTION_FILTER_GROUPING_COLLECTION)
            const response = await templateFiltersQuery.equalTo('productCode', productCode).first()
            if (response) {
                // Find only filters with "isActive":true and return it
                const filters = response.get('filters') ?? []
                for (const filter of filters) {
                    if ('items' in filter) {
                        filter.items = filter.items.filter((singleFilter) => singleFilter.isActive)
                    }
                }
                const result = filters.filter((filter) => {
                    if ('items' in filter) {
                        return filter.isActive && filter.items.length > 0
                    }
                    return filter.isActive
                })

                return result
            }
        } catch (error) {
            this.reportUIError(error, 'Parse.Service.getFiltersByProductCode', '')
        }

        return []
    }

    // TODO(cata): We should break down the Parse Service
    // into domain specific services with parse service as a base
    async validateMailingOrderDiscountCode(discountCodeParams: Params.MailingOrder.ValidateDiscountCode) {
        const fn: FunctionNames.MailingOrder.ValidateDiscountCode = 'validateMailingOrderDiscountCode'
        const response: Responses.MailingOrder.ValidateDiscountCode = await Parse.Cloud.run(fn, discountCodeParams)

        return response
    }

    async updateTemplateFilters() {
        const fn: FunctionNames.TemplateSelectionFilterGrouping.UpdateFilterGrouping = 'updateFilterGrouping'
        const response: Responses.TemplateSelectionFilterGrouping.UpdateFilterGrouping = await Parse.Cloud.run(fn, {})
        return response
    }

    async createOrUpdateWebhookConfiguration(establishmentId: string, url: string) {
        const fn: FunctionNames.WebhookConfiguration.CreateOrUpdate = 'createOrUpdateWebhookConfiguration'
        const params: Params.WebhookConfiguration.CreateOrUpdate = {
            url,
            establishmentId,
        }
        const response: Responses.WebhookConfiguration.CreateOrUpdate = await Parse.Cloud.run(fn, params)
        return response
    }

    getWebhookConfiguration(establishmentId: string) {
        const WEBHOOK_CONFIGURATION_COLLECTION: Objects.WebhookConfiguration.Collection = 'WebhookConfiguration'
        const establishmentPointer = this.createParseObject('Establishment', establishmentId)
        return new Parse.Query<Objects.WebhookConfiguration.Parse>(WEBHOOK_CONFIGURATION_COLLECTION).equalTo('establishment', establishmentPointer).first()
    }

    async findMyBestPlan(city: string, state: string, commission: number, annualProfit: number): Promise<Responses.Subscription.FindMyBestPlan> {
        const fn: FunctionNames.Subscription.FindMyBestPlan = 'findMyBestPlan'
        const params: Params.Subscription.FindMyBestPlan = {
            city,
            state,
            commission,
            annualProfit,
        }
        const response: Responses.Subscription.FindMyBestPlan = await Parse.Cloud.run(fn, params)
        return response
    }

    async getDesignTemplateProductConfiguration(
        productCode: Objects.Product.Code.Any
    ): Promise<Objects.UploadOwnDesignDesignTemplateProductConfiguration.Parse[] | undefined> {
        const DESIGN_TEMPLATE_PRODUCT_CONFIGURATION_COLLECTION: Objects.UploadOwnDesignDesignTemplateProductConfiguration.Collection =
            'UploadOwnDesignDesignTemplateProductConfiguration'

        const configurationResult = await new Parse.Query<Objects.UploadOwnDesignDesignTemplateProductConfiguration.Parse>(
            DESIGN_TEMPLATE_PRODUCT_CONFIGURATION_COLLECTION
        )
            .equalTo('productCode', productCode)
            .includeAll()
            .findAll()

        return configurationResult
    }

    async getSubscriptionPaymentMethods(establishmentId: string) {
        const fn: FunctionNames.Stripe.PaymentMethod.GetSubscriptionPaymentMethods = 'getSubscriptionPaymentMethods'
        const params: Params.Stripe.PaymentMethod.GetSubscriptionPaymentMethods = {
            establishmentId,
        }
        const response: Responses.Stripe.PaymentMethod.GetSubscriptionPaymentMethods = await Parse.Cloud.run(fn, params)
        return response
    }
}
